import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import entryGuideService from '../../services/entryGuideService';
import { supplierService } from '../../services/supplierService';
import { productService } from '../../services/productService';
import { getWarehouses } from '../../services/warehousesService';
import { getUnits } from '../../services/unitsService';
import { getProjects } from '../../services/projectsService';
import { getConversions } from '../../services/conversionsService';
import SupplierSelector from '../PurchaseOrders/SupplierSelector';
import ProductSelector from '../PurchaseOrders/ProductSelector';
import UnitSelect from '../Common/UnitSelect';
import LineItemField from '../Common/LineItemField';
import QuantityConversionHint from '../Common/QuantityConversionHint';
import CreateSupplierModal from '../PurchaseOrders/CreateSupplierModal';
import Toast from '../Common/Toast';
import FormLoadingSpinner from '../Common/FormLoadingSpinner';
import MachineryEventModal from '../Machinery/MachineryEventModal';
import { getCurrentDateLima, getCurrentTimeLima, formatDateToLima, formatDateLocaleLima, normalizeTime } from '../../utils/dateUtils';
import { formatBitacoraReading } from '../../utils/bitacoraFormat';
import { authService } from '../../services/authService';
import { filterWarehousesForUser } from '../../utils/limitedUserAccess';
import { downloadGuideItemsTemplate, parseGuideItemsExcel } from '../../utils/guideItemsExcelImport';
import { formatGuideSaveMessage } from '../../utils/kardexRecalculationHelpers';
import GuideItemsImportPreviewModal from './GuideItemsImportPreviewModal';
import { GuideDateField, GuideTimeField } from './GuideDateTimeFields';
import { parseFlexibleDateInput, parseFlexibleTimeInput } from '../../utils/dateUtils';
import PurchaseOrderProgressBar from '../PurchaseOrders/PurchaseOrderProgressBar';
import RequirementReferencePicker from '../Requirements/RequirementReferencePicker';
import { countPendingPurchaseOrderItems, getPurchaseOrderFulfillmentLabel } from '../../utils/purchaseOrderProgress';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import { useProductCatalog } from '../../hooks/useProductCatalog';
import {
  applyConvertedLineDisplay,
  findUnitInCatalog,
  getItemLineSubtotal,
  itemHasCustomConversion,
  unitsEquivalent,
} from '../../utils/unitSelectUtils';
import QuickProductLineBar from '../Common/QuickProductLineBar';
import ProductLineDefaultsBar from '../Common/ProductLineDefaultsBar';
import PricesIncludeIgvToggle from '../Common/PricesIncludeIgvToggle';
import PriceIgvHint from '../Common/PriceIgvHint';
import UnitPriceTextInput from '../Common/UnitPriceTextInput';
import { adjustNetPriceOnHasIgvChange, getGuideGrandTotalConIgvReferential, roundPrice } from '../../utils/priceIgvUtils';
import { emptyGuideStagingItem } from '../../utils/quickProductLineFactories';
import { getProductDisplayStock } from '../../utils/productStockDisplay';
import ReceptionConsumeButton from './ReceptionConsumeButton';
import ReceptionConsumeModal from './ReceptionConsumeModal';
import { GUIDE_ITEM_ROW, GIC } from '../../utils/guideItemRowLayout';
import { isReceptionConsumeAutomationActive } from '../../utils/receptionConsumeAutomation';
import { handlePreventEnterFormSubmit } from '../../utils/preventEnterFormSubmit';
import {
  analyzeEntryGuideReferenceChanges,
  buildEntryGuideReferenceSnapshot,
  buildReferenceEditInfoMessage,
  buildReferenceLossConfirmMessage,
  clearItemReferenceLinks,
  hasEntryGuideReference,
  itemHasReferenceLink,
  resolveSubmitReferenceIds,
} from '../../utils/entryGuideReferenceHelpers';
import {
  applyProductLineDefaults,
  buildProductLineSettings,
  emptyStagingWithDefaults,
  mergeProductLineDefaults,
} from '../../utils/productLineDefaults';

const ENTRY_GUIDE_IGV_RATE = 18;

const mergeUniqueById = (...lists) => {
  const map = new Map();
  lists.flat().forEach((item) => {
    if (item?.id != null) {
      map.set(Number(item.id), item);
    }
  });
  return Array.from(map.values());
};

const entryGuideFormReferencesManager = {
  loadingPromise: null,
  hasLoaded: false,
  loading: false,

  loadData(force = false) {
    if (force) {
      this.reset();
    }
    if (this.loading && this.loadingPromise) return this.loadingPromise;
    if (this.hasLoaded && this.loadingPromise) return this.loadingPromise;

    this.loading = true;
    this.loadingPromise = entryGuideService
      .getFormReferences()
      .then((res) => {
        this.hasLoaded = true;
        this.loading = false;
        const d = res.data || {};
        return {
          poRes: { success: true, data: d.pending_purchase_orders || [] },
          reqRes: { success: true, data: d.pending_requirements || [] },
          docRes: { success: true, data: d.pending_documents || [] },
        };
      })
      .catch((error) => {
        this.loading = false;
        this.loadingPromise = null;
        throw error;
      });

    return this.loadingPromise;
  },

  reset() {
    this.loadingPromise = null;
    this.hasLoaded = false;
    this.loading = false;
  },
};

const DECIMAL_INPUT_PATTERN = /^\d*\.?\d{0,4}$/;
const QUANTITY_INPUT_PATTERN = /^\d*\.?\d{0,3}$/;

const normalizeDecimalTyping = (raw) => String(raw ?? '').replace(',', '.').trim();

const isDecimalInputString = (value, pattern = DECIMAL_INPUT_PATTERN) => {
  if (value === '' || value === null || value === undefined) return true;
  return pattern.test(normalizeDecimalTyping(value));
};

const parseDecimalNumber = (value) => {
  if (value === '' || value === null || value === undefined) return 0;
  const n = parseFloat(normalizeDecimalTyping(value));
  return Number.isNaN(n) ? 0 : n;
};

const roundExitTransferUnitCost = (cost) => {
  const n = Number(cost);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 10000) / 10000;
};

/** Costo operativo kardex: conserva negativos (stock/costo negativo). */
const roundOperationalUnitCost = (cost) => {
  const n = Number(cost);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 10000) / 10000;
};

const applyOperationalCostToItem = (item, operationalCost) => {
  const unitCost = roundOperationalUnitCost(operationalCost);
  const qty = parseDecimalNumber(item.quantity) || 0;
  return {
    ...item,
    unit_price: unitCost,
    total: Math.round(qty * unitCost * 10000) / 10000,
  };
};

const applyReferenceCostToItem = (item, referenceCost) => {
  const refCost = roundExitTransferUnitCost(referenceCost);
  const qty = parseDecimalNumber(item.quantity) || 0;
  return {
    ...item,
    reference_unit_price: refCost,
    reference_total: Math.round(qty * refCost * 10000) / 10000,
  };
};

const applyExitTransferCostsToItem = (item, operationalCost, referenceCost = null) => {
  let next = applyOperationalCostToItem(item, operationalCost);
  if (referenceCost !== null && referenceCost !== undefined) {
    next = applyReferenceCostToItem(next, referenceCost);
  }
  return next;
};

const applyExitTransferCostToItem = (item, cost) => applyOperationalCostToItem(item, cost);

const pickDocumentCostFromCache = (cached) => {
  if (!cached) return { operational: 0, reference: 0 };
  return {
    operational: roundOperationalUnitCost(cached.average_cost ?? 0),
    reference: roundExitTransferUnitCost(cached.last_purchase_cost ?? 0),
  };
};

const applyCostsFromCache = (item, cached, useReference) => {
  const costs = pickDocumentCostFromCache(cached);
  if (useReference) {
    return applyExitTransferCostsToItem(item, costs.operational, costs.reference);
  }
  if (costs.operational <= 0) return item;
  return applyOperationalCostToItem(item, costs.operational);
};

const getItemReferenceDisplayTotal = (item) => {
  if (item.reference_total != null && item.reference_total !== '') {
    return parseFloat(item.reference_total) || 0;
  }
  const qty = parseDecimalNumber(item.quantity) || 0;
  const refUnit = parseFloat(item.reference_unit_price);
  if (!Number.isFinite(refUnit)) return 0;
  return Math.round(qty * refUnit * 10000) / 10000;
};

const syncReferenceLineTotal = (item) => {
  if (item.reference_unit_price == null || item.reference_unit_price === '') return item;
  if (typeof item.reference_unit_price === 'string') {
    const parsed = parseDecimalFieldInput(item.reference_unit_price);
    if (parsed.empty || parsed.invalid) return item;
    const qty = parseDecimalNumber(item.quantity) || 0;
    return {
      ...item,
      reference_total: Math.round(qty * parsed.value * 10000) / 10000,
    };
  }
  const refUnit = roundExitTransferUnitCost(item.reference_unit_price);
  const qty = parseDecimalNumber(item.quantity) || 0;
  return {
    ...item,
    reference_unit_price: refUnit,
    reference_total: Math.round(qty * refUnit * 10000) / 10000,
  };
};

const isReferenceCostEditable = (item) => {
  if (item?._reference_manual) return true;
  const ref = item?.reference_unit_price;
  if (ref === undefined || ref === null || ref === '') return true;
  if (typeof ref === 'string') return true;
  const n = Number(ref);
  return !Number.isFinite(n) || n <= 0;
};

/** Precio referencial ingresado manualmente: actualiza referencial y operativo (kardex). */
const applyManualReferenceCostToItem = (item, price) => {
  const unitCost = roundExitTransferUnitCost(price);
  const qty = parseDecimalNumber(item.quantity) || 0;
  const lineTotal = Math.round(qty * unitCost * 10000) / 10000;
  return {
    ...item,
    reference_unit_price: unitCost,
    reference_total: lineTotal,
    unit_price: unitCost,
    total: lineTotal,
    _reference_manual: true,
  };
};

const getItemFormDisplayTotal = (item, useReferenceMode) => (
  useReferenceMode ? getItemReferenceDisplayTotal(item) : (parseFloat(item.total) || 0)
);

const OperationalCostControl = ({
  unitPrice,
  editable,
  disabled,
  isOpen,
  onToggle,
  onChange,
  onBlur,
}) => {
  const display = formatDecimalFieldValue(unitPrice, DECIMAL_INPUT_PATTERN);

  return (
    <div className="relative inline-flex max-w-full">
      <button
        type="button"
        onClick={onToggle}
        className="truncate text-[10px] text-gray-400 hover:text-teal-600 underline decoration-dotted underline-offset-2"
        title="Costo operativo (kardex)"
      >
        Op.{display !== '' && Number(display) !== 0 ? ` ${display}` : ''}
      </button>
      {isOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-10 cursor-default"
            onClick={onToggle}
            aria-label="Cerrar costo operativo"
          />
          <div className="absolute left-0 top-full z-20 mt-0.5 w-36 rounded-md border border-gray-200 bg-white p-2 shadow-lg">
            <p className="mb-1 text-[10px] font-medium text-gray-500">Costo operativo</p>
            {editable && !disabled ? (
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={display}
                onChange={(e) => {
                  const raw = normalizeDecimalTyping(e.target.value);
                  if (raw === '' || DECIMAL_INPUT_PATTERN.test(raw)) {
                    onChange(raw);
                  }
                }}
                onBlur={(e) => onBlur(e.target.value)}
                className="w-full h-7 px-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                placeholder="0.0000"
              />
            ) : (
              <p className="text-xs text-gray-800 tabular-nums">{display !== '' ? display : '0'}</p>
            )}
            <p className="mt-1 text-[9px] leading-tight text-gray-400">Valor usado en kardex</p>
          </div>
        </>
      )}
    </div>
  );
};

const formatDecimalFieldValue = (value, pattern = DECIMAL_INPUT_PATTERN) => {
  if (value === '' || value === null || value === undefined) return '';
  if (typeof value === 'string' && isDecimalInputString(value, pattern)) {
    return normalizeDecimalTyping(value);
  }
  const n = Number(value);
  return Number.isNaN(n) ? '' : String(n);
};

const parseDecimalFieldInput = (raw, pattern = DECIMAL_INPUT_PATTERN) => {
  const normalized = normalizeDecimalTyping(raw);
  if (normalized === '') return { empty: true };
  if (!pattern.test(normalized)) return { invalid: true };
  const value = parseFloat(normalized);
  if (Number.isNaN(value)) return { invalid: true };
  return { value };
};

const EntryGuideForm = ({ isOpen, onClose, entryGuide = null, warehouseId = null, onSuccess, isPageMode = false, isInitial = false, isExit = false, isTransfer = false, isReception = false }) => {
  const {
    guidesRemoveStockRestrictions,
    exitReferenceLastPurchaseCostEnabled,
    productConversionsEnabled,
    quickProductLineEnabled,
    receptionConsumeButtonEnabled,
    receptionConsumeWarehouseIds,
    productsProjectEnabled,
    productsIgvEnabled,
  } = useModuleSettings();
  const [defaultLineProjectId, setDefaultLineProjectId] = useState('');
  const [defaultLineHasIgv, setDefaultLineHasIgv] = useState(true);
  const productLineSettings = useMemo(
    () => buildProductLineSettings({
      productsProjectEnabled,
      productsIgvEnabled,
      defaultProjectId: defaultLineProjectId,
      defaultHasIgv: defaultLineHasIgv,
    }),
    [productsProjectEnabled, productsIgvEnabled, defaultLineProjectId, defaultLineHasIgv],
  );
  const relaxStockRestrictions = guidesRemoveStockRestrictions && (isExit || isTransfer);
  const useReferenceLastPurchaseCost = exitReferenceLastPurchaseCostEnabled && (isExit || isTransfer || isReception);
  const isEntryGuide = !isInitial && !isExit && !isTransfer && !isReception;
  const showProjectField = isInitial || isExit;
  const showProductLineDefaults =
    (productsProjectEnabled && showProjectField) || (productsIgvEnabled && isEntryGuide);
  const [pricesIncludeIgv, setPricesIncludeIgv] = useState(false);

  const [formData, setFormData] = useState({
    warehouse_id: warehouseId || '',
    destination_warehouse_id: '',
    supplier_id: '',
    issue_date: getCurrentDateLima(),
    entry_date: getCurrentDateLima(),
    entry_time: getCurrentTimeLima(), // HH:mm en timezone de Lima
    received_by: '',
    notes: '',
    currency: 'PEN', // Por defecto soles
    purchase_order_id: '',
    requirement_id: '',
    document_id: '',
    transfer_guide_id: '',
    items: [],
  });

  const selectedWarehouseForCatalog = isReception
    ? null
    : (formData.warehouse_id || warehouseId || null);
  const {
    products,
    getProduct: getCatalogProduct,
    mergeProducts,
    ensureByIds,
    searchProducts,
    resolveIdentifiers,
  } = useProductCatalog(selectedWarehouseForCatalog);

  const findProductById = (id, fallback = null) => getCatalogProduct(id, fallback);

  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [units, setUnits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [conversions, setConversions] = useState([]);
  const [pendingPurchaseOrders, setPendingPurchaseOrders] = useState([]);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [pendingRequirements, setPendingRequirements] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  const [guideReady, setGuideReady] = useState(() => !entryGuide?.id);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [codePreview, setCodePreview] = useState('');
  const [importingItems, setImportingItems] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const guideItemsImportFileRef = useRef(null);
  const productSelectorRefs = useRef({});
  const stagingProductSelectorRef = useRef(null);
  const pendingFocusProductIndexRef = useRef(null);
  const [stagingItem, setStagingItem] = useState(() =>
    emptyGuideStagingItem({ isInitial, isExit, isTransfer, isReception }),
  );
  const [stagingError, setStagingError] = useState('');
  const [operationalCostOpenKey, setOperationalCostOpenKey] = useState(null);

  const showOperationalCostControl = useReferenceLastPurchaseCost && (isExit || isTransfer);
  useEffect(() => {
    if (!showProductLineDefaults) return;
    setStagingItem((prev) => mergeProductLineDefaults(prev, productLineSettings));
  }, [productLineSettings, showProductLineDefaults]);

  const [isCreateSupplierModalOpen, setIsCreateSupplierModalOpen] = useState(false);
  const [isOrderSelectorModalOpen, setIsOrderSelectorModalOpen] = useState(false);
  const [referencesLoading, setReferencesLoading] = useState(false);
  const [referencesLoaded, setReferencesLoaded] = useState(false);
  const [isTransferGuideSelectorModalOpen, setIsTransferGuideSelectorModalOpen] = useState(false);
  const [availableTransferGuides, setAvailableTransferGuides] = useState([]);
  const [selectedSupplierInModal, setSelectedSupplierInModal] = useState('');
  const [relatedPurchaseOrder, setRelatedPurchaseOrder] = useState(null);
  const [relatedDocument, setRelatedDocument] = useState(null);
  const [relatedRequirement, setRelatedRequirement] = useState(null);
  const [referenceSnapshot, setReferenceSnapshot] = useState(null);
  const [selectedTransferGuide, setSelectedTransferGuide] = useState(null);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [consumptionExitGuides, setConsumptionExitGuides] = useState([]);
  const [initialBalanceGuides, setInitialBalanceGuides] = useState([]);
  const [loadingConsumptionGuides, setLoadingConsumptionGuides] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  const [selectedGuideIds, setSelectedGuideIds] = useState([]);
  const openedReturnModalFromSelectRef = useRef(false); // true si se abrió desde "Seleccionar referencia" (editar), no revertir a compra al cerrar
  const [stockCostByProduct, setStockCostByProduct] = useState({}); // Precarga: productId -> { total_stock, stock_by_condition, average_cost }
  const [loadingStockCostBulk, setLoadingStockCostBulk] = useState(false);
  // Eventos de bitácora (solo guía de salida): un evento por ítem con product.machinery_register_enabled
  const [pendingMachineryEvents, setPendingMachineryEvents] = useState([]);
  const [eventModalItemIndex, setEventModalItemIndex] = useState(null);
  const [isConsumeModalOpen, setIsConsumeModalOpen] = useState(false);
  const [receptionConsumeTrace, setReceptionConsumeTrace] = useState(null);
  const [loadedMachineryEventsByItem, setLoadedMachineryEventsByItem] = useState([]);
  const [lastEventFormByItem, setLastEventFormByItem] = useState({});
  const hasLoadedFormData = useRef(false);
  const hasLoadedEntryGuide = useRef(false);
  const loadingStockInfo = useRef({}); // Para evitar llamadas duplicadas
  const lastBulkWarehouseId = useRef(null);
  const stockCostByProductRef = useRef({});
  stockCostByProductRef.current = stockCostByProduct;

  const warehouseForStock = formData.warehouse_id || warehouseId;
  const awaitingStockBulk = (isExit || isTransfer) && Boolean(warehouseForStock) && !relaxStockRestrictions;
  const isInitialLoading = !catalogLoaded || !guideReady || (awaitingStockBulk && loadingStockCostBulk);

  const productSelectorAsyncProps = useMemo(() => ({
    asyncSearch: true,
    knownProducts: products,
    onSearchProducts: searchProducts,
    onEnsureProduct: (id) => ensureByIds([id]),
    onProductPicked: (product) => mergeProducts([product]),
    productStockMapReady: !(isExit || isTransfer) || !formData.warehouse_id || !loadingStockCostBulk,
  }), [
    products,
    searchProducts,
    ensureByIds,
    mergeProducts,
    isExit,
    isTransfer,
    formData.warehouse_id,
    loadingStockCostBulk,
  ]);

  const allPendingPurchaseOrders = useMemo(
    () => mergeUniqueById(pendingPurchaseOrders, relatedPurchaseOrder ? [relatedPurchaseOrder] : []),
    [pendingPurchaseOrders, relatedPurchaseOrder],
  );
  const allPendingDocuments = useMemo(
    () => mergeUniqueById(pendingDocuments, relatedDocument ? [relatedDocument] : []),
    [pendingDocuments, relatedDocument],
  );
  const allPendingRequirements = useMemo(
    () => mergeUniqueById(pendingRequirements, relatedRequirement ? [relatedRequirement] : []),
    [pendingRequirements, relatedRequirement],
  );

  // Construir stock_info desde la precarga (misma estructura para input Costo Unit. y cuadro de stock)
  const buildStockInfoFromCache = (cached) => {
    if (!cached) return null;
    const sc = cached.stock_by_condition || {};
    const stockInfo = {
      total_stock: Number(cached.total_stock ?? 0),
      stock_by_condition: {
        good: Number(sc.good ?? 0),
        damaged: Number(sc.damaged ?? 0),
        defective: Number(sc.defective ?? 0),
        reusable: Number(sc.reusable ?? 0),
        non_reusable: Number(sc.non_reusable ?? 0),
      },
      average_cost: typeof cached.average_cost === 'number' && !Number.isNaN(cached.average_cost) ? cached.average_cost : 0,
    };
    return {
      ...stockInfo,
      total_stock: getProductDisplayStock(stockInfo),
    };
  };

  const applyWarehouseStockCostToItems = (items) => {
    if (!(isExit || isTransfer) || !formData.warehouse_id) return items;
    const cache = stockCostByProductRef.current;
    if (!cache || Object.keys(cache).length === 0) return items;

    return items.map((item) => {
      if (!item?.product_id) return item;
      const cached = cache[item.product_id] || cache[String(item.product_id)];
      if (!cached) return item;

      const stockInfo = buildStockInfoFromCache(cached);
      const costs = pickDocumentCostFromCache(cached);
      if (!useReferenceLastPurchaseCost && costs.operational <= 0) {
        return { ...item, stock_info: stockInfo };
      }

      return applyCostsFromCache({ ...item, stock_info: stockInfo }, cached, useReferenceLastPurchaseCost);
    });
  };

  // Debug: Verificar estado del modal
  useEffect(() => {
    console.log('Estado del modal de devolución:', isReturnModalOpen);
  }, [isReturnModalOpen]);

  // Precarga de costos por almacén: una sola llamada al seleccionar almacén (guía de salida o traspaso).
  // Al editar una guía (entryGuide), se pasa exclude_entry_guide_id para obtener el stock ANTES del registro de la guía.
  useEffect(() => {
    if (!(isExit || isTransfer) || !formData.warehouse_id) {
      if (!formData.warehouse_id) {
        setStockCostByProduct({});
        lastBulkWarehouseId.current = null;
      }
      return;
    }
    const whId = formData.warehouse_id;
    const excludeGuideId = entryGuide?.id ?? null;
    // Al editar: inventario justo antes del momento de la guía (fecha + hora)
    const asOfDate = entryGuide?.entry_date ? entryGuide.entry_date.split('T')[0] : null;
    const rawTime = entryGuide?.entry_time ?? '';
    const asOfTime = (rawTime && typeof rawTime === 'string') ? rawTime.substring(0, 5) : ''; // HH:mm
    const cacheKey = `${whId}-${excludeGuideId ?? 'new'}-${asOfDate ?? ''}-${asOfTime}`;
    if (lastBulkWarehouseId.current === cacheKey) return;
    lastBulkWarehouseId.current = cacheKey;
    setStockCostByProduct({});
    setLoadingStockCostBulk(true);
    console.log('[Guía-Costo] 1. Llamando API stock-cost-bulk para almacén:', whId, 'excluir guía:', excludeGuideId || '(ninguna)', 'as_of_date:', asOfDate, 'as_of_time:', asOfTime || '(ninguna)');
    entryGuideService.getProductStockAndCostBulk(whId, excludeGuideId, asOfDate, asOfTime)
      .then((response) => {
        console.log('[Guía-Costo] 2. Respuesta API stock-cost-bulk:', {
          success: response?.success,
          keysProductos: response?.data ? Object.keys(response.data) : [],
          muestraCosto: response?.data ? Object.entries(response.data).slice(0, 2).map(([id, v]) => ({ productId: id, average_cost: v?.average_cost, total_stock: v?.total_stock })) : null,
        });
        if (response.success && response.data) {
          setStockCostByProduct(response.data);
        } else {
          setStockCostByProduct({});
        }
      })
      .catch((err) => {
        console.log('[Guía-Costo] 2. Error en API stock-cost-bulk:', err?.message || err);
        setStockCostByProduct({});
      })
      .finally(() => setLoadingStockCostBulk(false));
  }, [isExit, isTransfer, formData.warehouse_id, entryGuide?.id]);

  // Cuando termina la precarga de stock/costo, rellenar costo en ítems que ya tenían producto seleccionado (salida/traspaso)
  // y asegurar stock_info en todos los ítems con producto para validar cantidad por condición (ej. no sacar más reutilizables de los que hay)
  useEffect(() => {
    if (!(isExit || isTransfer) || !formData.warehouse_id || Object.keys(stockCostByProduct).length === 0) return;
    const cache = stockCostByProduct;
    console.log('[Guía-Costo] 3. Re-aplicando costos: cache tiene', Object.keys(cache).length, 'productos');
    setFormData((prev) => {
      let changed = false;
      const newItems = prev.items.map((item, idx) => {
        const productId = item.product_id ? String(item.product_id) : null;
        if (!productId) return item;
        const cached = cache[productId];
        if (!cached) {
          if (productId) console.log('[Guía-Costo] 3. Ítem', idx, 'productId', productId, '-> sin datos en cache');
          return item;
        }
        const stockInfo = buildStockInfoFromCache(cached);
        // En edición (entryGuide existe), siempre actualizar stock_info con el stock ANTES de la guía (excluida).
        // Si no, solo actualizar cuando falta, para no pisar al crear.
        const shouldUpdateStockInfo = entryGuide?.id ? true : !item.stock_info;
        if (shouldUpdateStockInfo) {
          changed = true;
          item = { ...item, stock_info: stockInfo };
        }
        const costs = pickDocumentCostFromCache(cached);
        if (!useReferenceLastPurchaseCost) {
          const currentCost = typeof item.unit_price === 'number' && item.unit_price > 0 ? item.unit_price : null;
          if (currentCost !== null && currentCost > 0) return item;
          if (costs.operational <= 0) return item;
        }
        changed = true;
        return applyCostsFromCache({ ...item, stock_info: stockInfo }, cached, useReferenceLastPurchaseCost);
      });
      return changed ? { ...prev, items: newItems } : prev;
    });
  }, [isExit, isTransfer, formData.warehouse_id, stockCostByProduct, entryGuide?.id, useReferenceLastPurchaseCost]);

  // Captura rápida: aplicar costos operativo + referencial cuando carga el bulk de stock/costo
  useEffect(() => {
    if (!(isExit || isTransfer) || !formData.warehouse_id || !stagingItem.product_id) return;
    if (Object.keys(stockCostByProduct).length === 0) return;
    const productId = stagingItem.product_id;
    const cached = stockCostByProduct[productId] || stockCostByProduct[String(productId)];
    if (!cached) return;
    setStagingItem((prev) => {
      if (!prev.product_id) return prev;
      const stockInfo = buildStockInfoFromCache(cached);
      const next = applyCostsFromCache({ ...prev, stock_info: stockInfo }, cached, useReferenceLastPurchaseCost);
      if (
        next.unit_price === prev.unit_price
        && next.reference_unit_price === prev.reference_unit_price
        && next.reference_total === prev.reference_total
      ) {
        return prev;
      }
      return next;
    });
  }, [isExit, isTransfer, formData.warehouse_id, stockCostByProduct, stagingItem.product_id, useReferenceLastPurchaseCost]);

  // Recepción: costo referencial desde última compra global (sin depender del traspaso)
  const lastReceptionCostsKeyRef = useRef('');
  useEffect(() => {
    if (!useReferenceLastPurchaseCost || !isReception) return;
    const ids = [...new Set(formData.items.map((i) => i.product_id).filter(Boolean))];
    const key = ids.map(String).sort().join(',');
    if (!key || key === lastReceptionCostsKeyRef.current) return;
    lastReceptionCostsKeyRef.current = key;
    entryGuideService.getLastPurchaseCostBulk(ids)
      .then((res) => {
        if (!res?.success || !res.data) return;
        setFormData((prev) => ({
          ...prev,
          items: prev.items.map((item) => {
            if (!item.product_id) return item;
            const row = res.data[item.product_id] || res.data[String(item.product_id)];
            const cost = row?.last_purchase_cost ?? 0;
            return applyReferenceCostToItem(item, cost);
          }),
        }));
      })
      .catch(() => {});
  }, [useReferenceLastPurchaseCost, isReception, formData.items]);

  // Log: valor que recibe el input "Costo Unit." (solo salida/traspaso)
  useEffect(() => {
    if (!(isExit || isTransfer)) return;
    formData.items.forEach((item, idx) => {
      if (!item.product_id) return;
      const valorInput = item.unit_price ?? '';
      if (valorInput !== '' && valorInput !== undefined) {
        console.log('[Guía-Costo] 5. Input Costo Unit. ítem', idx, 'productId', item.product_id, '-> value:', valorInput);
      }
    });
  }, [isExit, isTransfer, formData.items]);

  // Cargar datos del formulario
  useEffect(() => {
    if (!(isOpen || isPageMode)) return;
    if (hasLoadedFormData.current) return;

    hasLoadedFormData.current = true;
    setCatalogLoaded(false);
    loadFormData();
  }, [isOpen, isPageMode]);

  useEffect(() => {
    const ids = (formData.items || [])
      .map((item) => item.product_id)
      .filter(Boolean);
    if (ids.length > 0) {
      ensureByIds(ids).catch(() => {});
    }
  }, [formData.items, ensureByIds]);

  // Al cerrar el modal, resetear para que al reabrir se recarguen catálogos y referencias
  useEffect(() => {
    if (!isOpen && !isPageMode) {
      entryGuideFormReferencesManager.reset();
      hasLoadedFormData.current = false;
      hasLoadedEntryGuide.current = false;
      setCatalogLoaded(false);
      setReferencesLoaded(false);
      setReferencesLoading(false);
      setGuideReady(true);
      setReferenceSnapshot(null);
    }
  }, [isOpen, isPageMode]);

  const applyFormReferences = (refs, related = {}) => {
    const {
      relatedPurchaseOrder: relatedPo = null,
      relatedDocument: relatedDoc = null,
      relatedRequirement: relatedReq = null,
    } = related;

    if (refs.poRes?.success) {
      setPendingPurchaseOrders(mergeUniqueById(refs.poRes.data || [], relatedPo ? [relatedPo] : []));
    }
    if (refs.reqRes?.success) {
      setPendingRequirements(mergeUniqueById(refs.reqRes.data || [], relatedReq ? [relatedReq] : []));
    }
    if (refs.docRes?.success) {
      setPendingDocuments(mergeUniqueById(refs.docRes.data || [], relatedDoc ? [relatedDoc] : []));
    }
    setReferencesLoaded(true);
  };

  const openOrderSelectorModal = () => {
    setIsOrderSelectorModalOpen(true);
    if (referencesLoaded || referencesLoading) return;

    setReferencesLoading(true);
    entryGuideFormReferencesManager
      .loadData()
      .then((refs) => {
        applyFormReferences(refs, {
          relatedPurchaseOrder,
          relatedDocument,
          relatedRequirement,
        });
      })
      .catch(() => {
        setToast({
          isOpen: true,
          message: 'Error al cargar las referencias pendientes',
          type: 'error',
        });
        setIsOrderSelectorModalOpen(false);
      })
      .finally(() => {
        setReferencesLoading(false);
      });
  };

  const loadFormData = async () => {
    try {
      const selectedWarehouseId = formData.warehouse_id || warehouseId || null;
      const res = await entryGuideService.getFormData(selectedWarehouseId);
      const d = res.data || {};
      if (res.success) {
        setWarehouses(
          filterWarehousesForUser(d.warehouses || [], authService.getCurrentUser())
        );
        setSuppliers(d.suppliers || []);
        setUnits(d.units || []);
        setProjects(d.projects || []);
        setConversions(d.conversions || []);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar los datos del formulario',
        type: 'error'
      });
    } finally {
      setCatalogLoaded(true);
    }
  };


  // Cargar guía de entrada si se pasa un ID (modo edición)
  useEffect(() => {
    if (entryGuide && typeof entryGuide === 'object' && entryGuide.id) {
      if (!hasLoadedEntryGuide.current) {
        hasLoadedEntryGuide.current = true;
        setGuideReady(false);
        loadEntryGuideData(entryGuide);
      }
      setGuideReady(true);
      return;
    }
    setGuideReady(true);
  }, [entryGuide]);

  useEffect(() => {
    if (!isReception) {
      setReceptionConsumeTrace(null);
      return;
    }
    if (entryGuide?.consume_exit_guide) {
      setReceptionConsumeTrace(entryGuide.consume_exit_guide);
      return;
    }
    if (entryGuide?.consume_exit_guide_id) {
      setReceptionConsumeTrace({ id: entryGuide.consume_exit_guide_id, code: null });
      return;
    }
    setReceptionConsumeTrace(null);
  }, [isReception, entryGuide?.consume_exit_guide, entryGuide?.consume_exit_guide_id]);

  const loadEntryGuideData = async (entryGuideData) => {
    // Formatear entry_time a HH:mm (sin segundos) en timezone de Lima
    let formattedEntryTime = getCurrentTimeLima();
    if (entryGuideData.entry_time) {
      formattedEntryTime = normalizeTime(entryGuideData.entry_time);
    }
    
    setFormData({
      warehouse_id: entryGuideData.warehouse_id || '',
      destination_warehouse_id: entryGuideData.destination_warehouse_id || '',
      supplier_id: entryGuideData.supplier_id || '',
      issue_date: entryGuideData.issue_date ? formatDateToLima(entryGuideData.issue_date) : getCurrentDateLima(),
      entry_date: entryGuideData.entry_date ? formatDateToLima(entryGuideData.entry_date) : getCurrentDateLima(),
      entry_time: formattedEntryTime,
      received_by: entryGuideData.received_by || '',
      notes: entryGuideData.notes || '',
      currency: (isInitial || isTransfer || isReception) ? null : (entryGuideData.currency || 'PEN'),
      purchase_order_id: entryGuideData.purchase_order_id || '',
      requirement_id: entryGuideData.requirement_id || '',
      document_id: entryGuideData.document_id || '',
      transfer_guide_id: entryGuideData.transfer_guide_id || '',
      items: entryGuideData.items?.map(item => ({
        id: item.id,
        product_id: item.product_id || '',
        description: item.description || '',
        project_id: item.project_id || '',
        purchase_order_item_id: item.purchase_order_item_id || null,
        purchase_order_id:
          item.purchase_order_item?.purchase_order_id
          || entryGuideData.purchase_order_id
          || null,
        document_item_id: item.document_item_id || null,
        requirement_item_id: item.requirement_item_id || null,
        requirement_id: item.requirement_item?.requirement_id || entryGuideData.requirement_id || null,
        kardex_event: item.kardex_event || (isInitial ? 'saldo_inicial' : (isExit ? 'venta' : (isTransfer ? 'traspaso' : (isReception ? 'recepcion' : 'compra')))),
        quantity: item.quantity,
        unit_price: Math.round((item.unit_price || 0) * 10000) / 10000,
        reference_unit_price: item.reference_unit_price != null && item.reference_unit_price !== ''
          ? Math.round((parseFloat(item.reference_unit_price) || 0) * 10000) / 10000
          : null,
        reference_total: item.reference_total != null && item.reference_total !== ''
          ? parseFloat(item.reference_total)
          : null,
        total: item.total || (item.quantity * (item.unit_price || 0)),
        unit_of_measure: item.unit_of_measure || '',
        condition: item.condition || 'good',
        has_igv: item.has_igv ?? item.purchase_order_item?.has_igv ?? item.document_item?.has_igv ?? true,
        consumption_exit_guide_id: item.consumption_exit_guide_id || null,
        consumption_exit_guide_code: item.consumption_exit_guide_code || item.consumption_exit_guide?.code || item.consumptionExitGuide?.code || '',
      })) || [],
    });

    // Si es recepción y tiene transfer_guide_id, cargar la información de la guía de traspaso
    if (isReception && entryGuideData.transfer_guide_id && entryGuideData.transfer_guide) {
      setSelectedTransferGuide(entryGuideData.transfer_guide);
    }

    // Guardar documentos relacionados para mostrar en la sección de referencias
    if (entryGuideData.purchase_order) {
      setRelatedPurchaseOrder(entryGuideData.purchase_order);
    }
    if (entryGuideData.document) {
      setRelatedDocument(entryGuideData.document);
    }
    if (entryGuideData.requirement) {
      setRelatedRequirement(entryGuideData.requirement);
    }

    if (entryGuideData.code) {
      setCodePreview(entryGuideData.code);
    }

    setReferenceSnapshot(buildEntryGuideReferenceSnapshot(entryGuideData));

    mergeProducts(
      (entryGuideData.items || [])
        .map((item) => item.product)
        .filter(Boolean),
    );

    const productIds = (entryGuideData.items || [])
      .map((item) => item.product_id)
      .filter(Boolean);
    if (productIds.length) {
      await ensureByIds(productIds);
    }
  };

  // Cuando es guía de salida y ya tenemos products + entryGuide con machinery_events, mapear eventos por índice de ítem
  useEffect(() => {
    if (!isExit || !entryGuide?.machinery_events?.length || !entryGuide?.items?.length) {
      if (isExit && entryGuide && !entryGuide.machinery_events?.length) setLoadedMachineryEventsByItem([]);
      return;
    }
    const itemIndicesWithEvent = entryGuide.items
      .map((it, idx) => idx)
      .filter((i) => {
        const it = entryGuide.items[i];
        const product = findProductById(it.product_id, it.product);
        return product?.machinery_register_enabled;
      });
    const loaded = [];
    const formByItem = {};
    entryGuide.machinery_events.forEach((ev, pos) => {
      const itemIndex = itemIndicesWithEvent[pos] ?? itemIndicesWithEvent[itemIndicesWithEvent.length - 1] ?? 0;
      const parts = [];
      if (ev.vehicle_id && ev.vehicle) parts.push(`Vehículo: ${ev.vehicle.code || ''} - ${ev.vehicle.name || ''}`);
      else if (ev.machine_id && ev.machine) parts.push(`Máquina: ${ev.machine.code || ''} - ${ev.machine.name || ''}`);
      if (ev.event_time) parts.push(String(ev.event_time).substring(0, 5));
      if (ev.hours != null) parts.push(`${formatBitacoraReading(ev.hours)} h`);
      if (ev.kilometers != null) parts.push(`${formatBitacoraReading(ev.kilometers)} km`);
      if (ev.conductor?.name) parts.push(ev.conductor.name);
      if (ev.event_type) parts.push(typeof ev.event_type === 'string' ? ev.event_type : (ev.event_type?.name || ''));
      if (ev.project?.name) parts.push(ev.project.name);
      const displaySummary = parts.length ? parts.join(' · ') : 'Evento';
      loaded.push({ itemIndex, displaySummary, eventId: ev.id });
      formByItem[itemIndex] = {
        event_date: ev.event_date ? (ev.event_date.includes?.('T') ? ev.event_date.split('T')[0] : ev.event_date) : '',
        event_time: ev.event_time ? String(ev.event_time).substring(0, 5) : '',
        record_type: ev.vehicle_id ? 'vehicle' : 'machine',
        machine_id: ev.machine_id != null ? String(ev.machine_id) : '',
        vehicle_id: ev.vehicle_id != null ? String(ev.vehicle_id) : '',
        hours: ev.hours != null ? ev.hours : '',
        kilometers: ev.kilometers != null ? ev.kilometers : '',
        operator: (ev.conductor?.name ?? ev.operator) ?? '',
        event_type: typeof ev.event_type === 'string' ? ev.event_type : (ev.event_type?.name ?? ''),
        project_id: ev.project_id != null ? String(ev.project_id) : '',
        notes: ev.notes ?? ''
      };
    });
    setLoadedMachineryEventsByItem(loaded);
    setLastEventFormByItem((prev) => ({ ...prev, ...formByItem }));
  }, [isExit, entryGuide?.id, entryGuide?.machinery_events, entryGuide?.items, products]);

  const handleSupplierCreated = async (newSupplier) => {
    try {
      const response = await supplierService.getSuppliers({ active_only: true });
      if (response.success && response.data) {
        setSuppliers(response.data);
      }
    } catch (error) {
      console.error('Error al recargar entidades:', error);
      setSuppliers(prev => [...prev, newSupplier]);
    }
    
    setFormData(prev => ({
      ...prev,
      supplier_id: newSupplier.id.toString(),
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (isTransfer && name === 'warehouse_id' && String(value) === String(prev.destination_warehouse_id)) {
        next.destination_warehouse_id = '';
      }
      return next;
    });
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleDateTimeValidationError = (name, message) => {
    if (message) {
      setErrors((prev) => ({ ...prev, [name]: message }));
    } else if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const findPurchaseOrderForItem = (item) => {
    const allOrders = [
      ...allPendingPurchaseOrders,
    ];
    if (item?.purchase_order_id) {
      const byId = allOrders.find((po) => Number(po.id) === Number(item.purchase_order_id));
      if (byId) return byId;
    }
    if (item?.purchase_order_item_id) {
      return (
        allOrders.find((po) =>
          (po.items || []).some((oi) => Number(oi.id) === Number(item.purchase_order_item_id)),
        ) || null
      );
    }
    return null;
  };

  const resolveUseConvertedQuantityForSubtotal = (item) => {
    if (!productConversionsEnabled) return false;
    const order = findPurchaseOrderForItem(item);
    if (!order) return false;
    return order.use_converted_quantity_for_subtotal !== false;
  };

  const applySubtotalModeDisplay = (lineItem, { useConverted } = {}) => {
    const product = lineItem.product_id
      ? findProductById(lineItem.product_id, lineItem.product)
      : null;
    const sourceUnit = lineItem._source_unit_of_measure ?? lineItem.unit_of_measure ?? '';
    const sourceQty = lineItem._source_quantity ?? lineItem.quantity ?? 0;
    const useConvertedFlag = useConverted ?? resolveUseConvertedQuantityForSubtotal(lineItem);
    const display = applyConvertedLineDisplay({
      product,
      unitCode: sourceUnit,
      quantity: sourceQty,
      productConversionsEnabled,
      useConvertedQuantityForSubtotal: useConvertedFlag,
      units,
    });
    return {
      ...lineItem,
      _source_quantity: sourceQty,
      _source_unit_of_measure: sourceUnit,
      quantity: display.quantity,
      unit_of_measure: display.unitCode,
      uses_converted_display: display.usesConvertedDisplay,
    };
  };

  const resolveItemLineTotal = (item) => {
    const qty = parseDecimalNumber(item.quantity);
    const pu = parseDecimalNumber(item.unit_price);
    if (item?.uses_converted_display) {
      const sourceQty = parseDecimalNumber(item._source_quantity ?? item.quantity);
      const useConverted = resolveUseConvertedQuantityForSubtotal(item);
      if (useConverted) {
        return Math.round(qty * pu * 10000) / 10000;
      }
      return Math.round(sourceQty * pu * 10000) / 10000;
    }
    if (!productConversionsEnabled) {
      return Math.round(qty * pu * 10000) / 10000;
    }
    const product = findProductById(item.product_id, item.product);
    return (
      Math.round(
        getItemLineSubtotal({
          product,
          unitCode: item.unit_of_measure,
          quantity: qty,
          unitPrice: pu,
          productConversionsEnabled,
          useConvertedQuantityForSubtotal: resolveUseConvertedQuantityForSubtotal(item),
          units,
        }) * 10000,
      ) / 10000
    );
  };

  const applyStagingProductStock = (item, productId) => {
    if (!(isExit || isTransfer) || !formData.warehouse_id || !productId) {
      return item;
    }
    const cache = stockCostByProductRef.current;
    const cached = cache[productId] || cache[String(productId)];
    const unitOfMeasure = findProductById(productId)?.unit_of_measure || '';
    if (cached) {
      const stockInfo = buildStockInfoFromCache(cached);
      if (!relaxStockRestrictions && getProductDisplayStock(stockInfo) <= 0) {
        setToast({
          isOpen: true,
          message: 'No puedes seleccionar este producto porque no hay stock disponible',
          type: 'error',
        });
        return {
          ...item,
          product_id: '',
          unit_price: 0,
          total: 0,
          reference_unit_price: null,
          reference_total: null,
          stock_info: null,
          unit_of_measure: '',
        };
      }
      const costs = pickDocumentCostFromCache(cached);
      return {
        ...applyCostsFromCache(item, cached, useReferenceLastPurchaseCost),
        product_id: productId,
        unit_of_measure: unitOfMeasure,
        stock_info: stockInfo,
      };
    }
    return {
      ...item,
      product_id: productId,
      unit_of_measure: unitOfMeasure,
      unit_price: 0,
      total: 0,
      reference_unit_price: null,
      reference_total: null,
      stock_info: null,
    };
  };

  const getStagingDisplayUnitPrice = (item) => {
    const showRef = useReferenceLastPurchaseCost && (isExit || isTransfer || isReception);
    if (showRef) {
      const ref = item.reference_unit_price;
      if (ref !== undefined && ref !== null && ref !== '') {
        return formatDecimalFieldValue(ref, DECIMAL_INPUT_PATTERN);
      }
      return '0';
    }
    return formatDecimalFieldValue(item.unit_price, DECIMAL_INPUT_PATTERN);
  };

  const commitManualReferenceCost = (item, rawValue) => {
    const parsed = parseDecimalFieldInput(rawValue);
    if (parsed.empty) {
      return {
        ...item,
        reference_unit_price: '',
        reference_total: null,
        unit_price: 0,
        total: 0,
      };
    }
    if (parsed.invalid) return item;
    return applyManualReferenceCostToItem(item, parsed.value);
  };

  const commitOperationalCostOnly = (item, rawValue) => {
    const parsed = parseDecimalFieldInput(rawValue);
    if (parsed.empty) {
      return applyOperationalCostToItem(item, 0);
    }
    if (parsed.invalid) return item;
    return applyOperationalCostToItem(item, parsed.value);
  };

  const renderOperationalCostHint = (item, key, { disabled = false } = {}) => {
    if (!showOperationalCostControl) return null;
    return (
      <OperationalCostControl
        unitPrice={item.unit_price}
        editable={useReferenceLastPurchaseCost}
        disabled={disabled}
        isOpen={operationalCostOpenKey === key}
        onToggle={() => setOperationalCostOpenKey((prev) => (prev === key ? null : key))}
        onChange={(raw) => {
          if (key === 'staging') {
            handleStagingChange('unit_price', raw);
            return;
          }
          handleItemChange(key, 'unit_price', raw);
        }}
        onBlur={(raw) => {
          if (key === 'staging') {
            setStagingItem((prev) => commitOperationalCostOnly(prev, raw));
            return;
          }
          setFormData((prev) => {
            const newItems = [...prev.items];
            newItems[key] = commitOperationalCostOnly(newItems[key], raw);
            return { ...prev, items: newItems };
          });
        }}
      />
    );
  };

  const handleStagingChange = (field, value) => {
    setStagingError('');
    setStagingItem((prev) => {
      let updated = { ...prev };
      if (field === 'product_id') {
        updated.uses_converted_display = false;
        delete updated._source_quantity;
        delete updated._source_unit_of_measure;
        if (value) {
          updated.product_id = String(value);
          const product = findProductById(value);
          if (product) {
            updated.description = product.name || '';
            if (product.unit_of_measure) {
              updated.unit_of_measure = product.unit_of_measure;
            }
            const defaults = applyProductLineDefaults(productLineSettings);
            if (defaults.project_id !== undefined && showProjectField) {
              updated.project_id = defaults.project_id;
            }
            if (defaults.has_igv !== undefined && isEntryGuide) {
              const oldHasIgv = prev.has_igv ?? true;
              updated.has_igv = defaults.has_igv;
              if (isEntryGuide && oldHasIgv !== defaults.has_igv) {
                updated.unit_price = adjustNetPriceOnHasIgvChange(prev.unit_price, {
                  oldHasIgv,
                  newHasIgv: defaults.has_igv,
                  pricesIncludeIgv,
                  igvRate: ENTRY_GUIDE_IGV_RATE,
                });
              }
            }
          }
          updated = applyStagingProductStock(updated, value);
          if (!updated.product_id) {
            updated.product_id = String(value);
          }
        } else {
          updated.product_id = '';
          updated.description = '';
          updated.unit_of_measure = '';
          updated.unit_price = 0;
          updated.total = 0;
          updated.reference_unit_price = null;
          updated.reference_total = null;
          updated.stock_info = null;
        }
        return updated;
      }
      if (field === 'unit_of_measure') {
        updated.uses_converted_display = false;
        delete updated._source_quantity;
        delete updated._source_unit_of_measure;
      }
      if (isEntryGuide && field === 'has_igv') {
        const newHasIgv = value === true || value === 'true';
        const oldHasIgv = prev.has_igv ?? true;
        updated.has_igv = newHasIgv;
        if (oldHasIgv !== newHasIgv) {
          updated.unit_price = adjustNetPriceOnHasIgvChange(prev.unit_price, {
            oldHasIgv,
            newHasIgv,
            pricesIncludeIgv,
            igvRate: ENTRY_GUIDE_IGV_RATE,
          });
        }
      } else if (isEntryGuide && field === 'unit_price') {
        updated.unit_price = value === '' || value === null || value === undefined
          ? ''
          : roundPrice(parseFloat(value) || 0);
      } else if (field === 'reference_unit_price') {
        updated.reference_unit_price = value === '' || value === null || value === undefined
          ? ''
          : (typeof value === 'string' ? normalizeDecimalTyping(value) : value);
        if (value !== '' && value !== null && value !== undefined) {
          updated._reference_manual = true;
        }
      } else {
        updated = {
          ...updated,
          [field]: field === 'quantity' || field === 'unit_price'
            ? (value === '' || value === null || value === undefined
              ? ''
              : (typeof value === 'string' ? normalizeDecimalTyping(value) : value))
            : value,
        };
      }
      if (field === 'quantity' || field === 'unit_price' || field === 'reference_unit_price' || field === 'unit_of_measure' || (isEntryGuide && field === 'has_igv')) {
        updated.total = resolveItemLineTotal(updated);
        if (useReferenceLastPurchaseCost && (isExit || isTransfer || isReception)) {
          updated = syncReferenceLineTotal(updated);
        }
      }
      return updated;
    });
  };

  const handleQuickAddProduct = () => {
    if (!stagingItem.product_id) {
      setStagingError('Seleccione un producto');
      return;
    }
    const qty = parseDecimalNumber(stagingItem.quantity);
    if (!qty || qty <= 0) {
      setStagingError('La cantidad debe ser mayor a 0');
      return;
    }
    if (isEntryGuide && parseDecimalNumber(stagingItem.unit_price) <= 0) {
      setStagingError('El costo unitario debe ser mayor a 0');
      return;
    }
    if (useReferenceLastPurchaseCost && (isExit || isTransfer || isReception) && parseDecimalNumber(stagingItem.reference_unit_price) <= 0) {
      setStagingError('Ingrese un costo unitario referencial mayor a 0');
      return;
    }
    if (!relaxStockRestrictions && (isExit || isTransfer)) {
      const stockInfo = stagingItem.stock_info
        || (stockCostByProductRef.current[stagingItem.product_id]
          ? buildStockInfoFromCache(
            stockCostByProductRef.current[stagingItem.product_id]
              || stockCostByProductRef.current[String(stagingItem.product_id)],
          )
          : null);
      if (stockInfo) {
        const cond = stagingItem.condition || 'good';
        const available = Number(stockInfo.stock_by_condition?.[cond]) || 0;
        if (qty > available) {
          setStagingError(`La cantidad excede el stock disponible (${available.toFixed(3)})`);
          return;
        }
      }
    }
    const itemToAdd = syncReferenceLineTotal({
      ...stagingItem,
      quantity: typeof stagingItem.quantity === 'string'
        ? (parseDecimalNumber(stagingItem.quantity) || stagingItem.quantity)
        : stagingItem.quantity,
      unit_price: parseDecimalNumber(stagingItem.unit_price),
      total: resolveItemLineTotal(stagingItem),
    });
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, itemToAdd],
    }));
    setStagingItem(emptyStagingWithDefaults(
      emptyGuideStagingItem,
      productLineSettings,
      { isInitial, isExit, isTransfer, isReception },
    ));
    setStagingError('');
    window.setTimeout(() => stagingProductSelectorRef.current?.focus?.(), 0);
  };

  // Seleccionar orden de compra
  const handleSelectPurchaseOrder = (orderId) => {
    const order = allPendingPurchaseOrders.find((po) => po.id === orderId);
    if (!order) return;

    // Mostrar referencia seleccionada en el formulario (también en creación).
    setRelatedPurchaseOrder(order);
    setRelatedDocument(null);

    setFormData(prev => {
      const newItems = [...prev.items];
      
      if (!prev.supplier_id && order.supplier_id) {
        prev.supplier_id = order.supplier_id.toString();
      }

      if (order.items) {
        order.items.forEach(item => {
          // Solo agregar items que tienen cantidad pendiente
          const pendingQty = parseFloat(item.pending_quantity || 0);
          if (pendingQty > 0 && !newItems.find(i => i.purchase_order_item_id === item.id)) {
            // Obtener la descripción: primero del item, luego del producto relacionado
            let description = item.description || '';
            if (!description && item.product?.name) {
              description = item.product.name;
            }
            
            const unitPrice = Math.round((item.unit_price || 0) * 10000) / 10000;
            const rawUnit = item.unit_of_measure || item.product?.unit_of_measure || '';
            const useConverted = order.use_converted_quantity_for_subtotal !== false;
            const lineItem = applySubtotalModeDisplay({
              purchase_order_item_id: item.id,
              purchase_order_id: orderId,
              product_id: item.product_id,
              product: item.product,
              description: description,
              project_id: item.project_id || '',
              kardex_event: 'compra',
              quantity: pendingQty,
              unit_price: unitPrice,
              unit_of_measure: rawUnit,
              condition: 'good',
              _source_quantity: pendingQty,
              _source_unit_of_measure: rawUnit,
            }, { useConverted });
            newItems.push({
              ...lineItem,
              has_igv: item.has_igv ?? true,
              total: resolveItemLineTotal(lineItem),
            });
          }
        });
      }

      return {
        ...prev,
        purchase_order_id: orderId,
        document_id: '',
        requirement_id: '',
        items: newItems,
      };
    });

    mergeProducts((order.items || []).map((item) => item.product).filter(Boolean));
    setRelatedRequirement(null);
    setIsOrderSelectorModalOpen(false);
  };

  const handleSelectRequirement = (reqId) => {
    const req = allPendingRequirements.find((r) => r.id === reqId);
    if (!req) return;

    setRelatedRequirement(req);
    setRelatedPurchaseOrder(null);
    setRelatedDocument(null);

    setFormData((prev) => {
      const newItems = [...prev.items];

      if (req.items) {
        req.items.forEach((ri) => {
          const pendingQty = parseFloat(ri.pending_quantity ?? ri.quantity ?? 0);
          if (pendingQty <= 0.001) return;
          if (newItems.find((i) => i.requirement_item_id === ri.id)) return;

          let description = ri.product?.name || '';
          newItems.push({
            requirement_item_id: ri.id,
            requirement_id: reqId,
            product_id: ri.product_id,
            description,
            project_id: ri.project_id || '',
            kardex_event: isInitial ? 'saldo_inicial' : isExit ? 'venta' : isTransfer ? 'traspaso' : isReception ? 'recepcion' : 'compra',
            quantity: pendingQty,
            unit_price: 0,
            total: 0,
            unit_of_measure: ri.unit_of_measure || ri.product?.unit_of_measure || '',
            condition: 'good',
          });
        });
      }

      return {
        ...prev,
        requirement_id: reqId,
        purchase_order_id: '',
        document_id: '',
        items: newItems,
      };
    });

    setIsOrderSelectorModalOpen(false);
    setToast({
      isOpen: true,
      message: `Ítems cargados desde ${req.code}. Indique costos si aplica.`,
      type: 'success',
    });
  };

  // Seleccionar documento
  const handleSelectDocument = (documentId) => {
    const document = allPendingDocuments.find((doc) => doc.id === documentId);
    if (!document) return;

    // Mostrar referencia seleccionada en el formulario (también en creación).
    setRelatedDocument(document);
    setRelatedPurchaseOrder(null);
    setRelatedRequirement(null);

    setFormData((prev) => {
      const newItems = [...prev.items];
      
      if (!prev.supplier_id && document.supplier_id) {
        prev.supplier_id = document.supplier_id.toString();
      }

      const deliveryGuideDate = document.delivery_date ? formatDateToLima(document.delivery_date) : null;

      if (document.items) {
        document.items.forEach((item) => {
          if (item.item_type !== 'product') return;

          const pendingQty = parseFloat(item.pending_quantity ?? 0);
          if (pendingQty <= 0.001) return;
          if (newItems.find((i) => i.document_item_id === item.id)) return;

            // Obtener la descripción: primero del item, luego del producto relacionado
            let description = item.description || '';
            if (!description && item.product?.name) {
              description = item.product.name;
            }
            
            const unitPrice = Math.round((item.unit_price || 0) * 10000) / 10000;
            const rawUnit = item.unit_of_measure || item.product?.unit_of_measure || '';
            const fullQty = parseFloat(item.quantity || 0);
            const useConverted = document.use_converted_quantity_for_subtotal !== false;
            const lineItem = applySubtotalModeDisplay({
              document_item_id: item.id,
              document_id: documentId,
              purchase_order_item_id: item.purchase_order_item_id || null,
              purchase_order_id: item.purchase_order_item?.purchase_order_id || null,
              product_id: item.product_id,
              product: item.product,
              description: description,
              project_id: item.project_id || '',
              kardex_event: 'compra',
              quantity: pendingQty,
              unit_price: unitPrice,
              unit_of_measure: rawUnit,
              condition: 'good',
              _source_quantity: pendingQty,
              _source_unit_of_measure: rawUnit,
            }, { useConverted });
            const storedSubtotal = parseFloat(item.subtotal);
            const useStoredSubtotal =
              Number.isFinite(storedSubtotal) &&
              storedSubtotal >= 0 &&
              !lineItem.uses_converted_display &&
              fullQty > 0 &&
              Math.abs(pendingQty - fullQty) < 0.001;
            newItems.push({
              ...lineItem,
              has_igv: item.has_igv ?? true,
              total: useStoredSubtotal
                ? Math.round(storedSubtotal * 10000) / 10000
                : resolveItemLineTotal(lineItem),
            });
        });
      }

      return {
        ...prev,
        document_id: documentId,
        requirement_id: '',
        purchase_order_id: '',
        ...(deliveryGuideDate
          ? { issue_date: deliveryGuideDate, entry_date: deliveryGuideDate }
          : {}),
        items: newItems,
      };
    });

    mergeProducts((document.items || []).map((item) => item.product).filter(Boolean));
    setIsOrderSelectorModalOpen(false);
    setToast({
      isOpen: true,
      message: `Ítems pendientes cargados desde ${document.code}.`,
      type: 'success',
    });
  };

  // Quitar referencia (OC/Documento/REQ) y eliminar sus ítems jalados del detalle.
  const handleClearReference = () => {
    setFormData((prev) => {
      const filteredItems = (prev.items || []).filter((item) => {
        const fromPurchaseOrder = !!item.purchase_order_item_id;
        const fromDocument = !!item.document_item_id;
        const fromRequirement = !!item.requirement_item_id;
        return !fromPurchaseOrder && !fromDocument && !fromRequirement;
      });

      return {
        ...prev,
        purchase_order_id: '',
        document_id: '',
        requirement_id: '',
        items: filteredItems,
      };
    });

    setRelatedPurchaseOrder(null);
    setRelatedDocument(null);
    setRelatedRequirement(null);
    setToast({
      isOpen: true,
      message: 'Referencia eliminada y productos vinculados retirados.',
      type: 'info',
    });
  };

  // Agregar producto manual
  const handleAddProduct = () => {
    setFormData(prev => {
      pendingFocusProductIndexRef.current = prev.items.length;
      return {
        ...prev,
        items: [...prev.items, {
          product_id: '',
          description: '',
          project_id: '',
          kardex_event: isInitial ? 'saldo_inicial' : (isExit ? 'venta' : (isTransfer ? 'traspaso' : (isReception ? 'recepcion' : 'compra'))),
          quantity: 1,
          unit_price: 0,
          total: 0,
          unit_of_measure: '',
          condition: 'good',
          has_igv: true,
        }],
      };
    });
  };

  useEffect(() => {
    const idx = pendingFocusProductIndexRef.current;
    if (idx === null || idx === undefined) return;

    const focusProductField = () => {
      const selector = productSelectorRefs.current[idx];
      if (!selector?.focus) return false;
      selector.focus();
      pendingFocusProductIndexRef.current = null;
      return true;
    };

    if (focusProductField()) return;

    const timerId = window.setTimeout(() => {
      focusProductField();
    }, 0);

    return () => window.clearTimeout(timerId);
  }, [formData.items]);

  const handleDownloadGuideItemsTemplate = () => {
    downloadGuideItemsTemplate({ isInitial, isExit, isTransfer, isReception });
  };

  const handlePickGuideItemsImport = () => {
    guideItemsImportFileRef.current?.click();
  };

  const handleGuideItemsImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const nameOk = /\.xlsx$/i.test(file.name || '') || (file.type || '').includes('spreadsheetml');
    if (!nameOk) {
      setToast({
        isOpen: true,
        message: 'Use un archivo Excel .xlsx (misma estructura que la plantilla descargada).',
        type: 'error',
      });
      return;
    }
    if (!catalogLoaded) {
      setToast({
        isOpen: true,
        message: 'Espere a que cargue el formulario antes de importar.',
        type: 'warning',
      });
      return;
    }
    setImportingItems(true);
    try {
      const { items: parsed, errors, warnings } = await parseGuideItemsExcel(file, {
        products,
        resolveProductIdentifiers: resolveIdentifiers,
        units,
        projects,
        isInitial,
        isExit,
        isTransfer,
        isReception,
        productConversionsEnabled,
      });
      if (parsed.length === 0 && !errors.length) {
        setToast({ isOpen: true, message: 'No se importó ninguna fila con datos.', type: 'warning' });
        return;
      }
      setImportPreview({
        fileName: file.name,
        items: parsed,
        errors: errors || [],
        warnings: warnings || [],
      });
    } catch (err) {
      setToast({
        isOpen: true,
        message: err?.response?.data?.message || err?.message || 'Error al leer el archivo Excel.',
        type: 'error',
      });
    } finally {
      setImportingItems(false);
    }
  };

  const handleConfirmImportPreview = (confirmedRows) => {
    const toAdd = (confirmedRows || importPreview?.items || []).map(
      ({ _unitFormula, _unitIsCustom, ...item }) => item,
    );
    if (!toAdd.length) {
      setImportPreview(null);
      return;
    }
    const warnings = importPreview?.warnings || [];
    const enriched = applyWarehouseStockCostToItems(toAdd);
    setFormData((prev) => ({
      ...prev,
      items: [...(prev.items || []), ...enriched],
    }));
    setImportPreview(null);
    const wMsg = warnings.length
      ? ` Advertencias: ${warnings.slice(0, 3).join(' ')}${warnings.length > 3 ? '…' : ''}`
      : '';
    setToast({
      isOpen: true,
      message: `Se añadieron ${toAdd.length} fila(s) a la guía.${wMsg}`,
      type: 'info',
    });
  };

  // Atajo de teclado: Ctrl+Alt+P = Agregar producto
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.ctrlKey && e.altKey && e.key.toLowerCase() === 'p') {
        const target = e.target;
        if (/^(INPUT|TEXTAREA|SELECT)$/.test(target?.tagName) || target?.isContentEditable) return;
        if (entryGuide?.document_id) return;
        e.preventDefault();
        if (quickProductLineEnabled) {
          stagingProductSelectorRef.current?.focus?.();
        } else {
          handleAddProduct();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [entryGuide?.document_id, quickProductLineEnabled]);

  // Cambiar item
  const handleItemChange = (index, field, value) => {
    if (field === 'product_id' && value) {
      const currentItem = formData.items[index];
      if (
        currentItem
        && itemHasReferenceLink(currentItem)
        && String(value) !== String(currentItem.product_id)
      ) {
        const confirmed = window.confirm(
          'Este producto está vinculado a una referencia (OC, documento o requerimiento). Si lo cambia, se perderá ese vínculo en esta línea. ¿Continuar?',
        );
        if (!confirmed) return;
      }
    }

    setFormData(prev => {
      const newItems = [...prev.items];
      const currentItem = newItems[index];
      
      if (field === 'product_id') {
        currentItem.uses_converted_display = false;
        delete currentItem._source_quantity;
        delete currentItem._source_unit_of_measure;
        if (
          value
          && itemHasReferenceLink(currentItem)
          && String(value) !== String(currentItem.product_id)
        ) {
          Object.assign(currentItem, clearItemReferenceLinks(currentItem));
        }
      }

      if (field === 'product_id' && value) {
        const product = findProductById(value);
        if (product) {
          currentItem.unit_of_measure = product.unit_of_measure || '';
          const defaults = applyProductLineDefaults(productLineSettings);
          if (defaults.project_id !== undefined && showProjectField) {
            currentItem.project_id = defaults.project_id;
          }
          if (defaults.has_igv !== undefined && isEntryGuide) {
            currentItem.has_igv = defaults.has_igv;
          }
        }

        // Para guías de salida y traspasos: usar precarga (stockCostByProduct) y aplicar costo en esta misma actualización (evitar setFormData anidado)
        if ((isExit || isTransfer) && prev.warehouse_id && value) {
          const productId = value;
          const cache = stockCostByProductRef.current;
          const cached = cache[productId] || cache[String(productId)];
          const unitOfMeasure = findProductById(value)?.unit_of_measure || '';

          if (cached) {
            const stockInfo = buildStockInfoFromCache(cached);
            if (!relaxStockRestrictions && getProductDisplayStock(stockInfo) <= 0) {
              setToast({
                isOpen: true,
                message: 'No puedes seleccionar este producto porque no hay stock disponible',
                type: 'error'
              });
              newItems[index] = {
                ...currentItem,
                product_id: '',
                unit_price: 0,
                total: 0,
                stock_info: null,
                unit_of_measure: '',
              };
              return { ...prev, items: newItems };
            }
            const costs = pickDocumentCostFromCache(cached);
            newItems[index] = {
              ...applyCostsFromCache(currentItem, cached, useReferenceLastPurchaseCost),
              product_id: value,
              unit_of_measure: unitOfMeasure,
              stock_info: stockInfo,
            };
            return { ...prev, items: newItems };
          }

          // Sin datos en cache (producto sin movimientos en almacén)
          newItems[index] = {
            ...currentItem,
            product_id: value,
            unit_of_measure: unitOfMeasure,
            unit_price: 0,
            total: 0,
            reference_unit_price: useReferenceLastPurchaseCost ? 0 : null,
            reference_total: useReferenceLastPurchaseCost ? 0 : null,
            stock_info: null,
          };
          return { ...prev, items: newItems };
        }
      }
      
      if (field === 'unit_of_measure') {
        currentItem.uses_converted_display = false;
        delete currentItem._source_quantity;
        delete currentItem._source_unit_of_measure;
      }

      let updatedItem = { ...currentItem };

      if (field === 'quantity') {
        updatedItem.quantity = value === '' || value === null || value === undefined
          ? ''
          : (typeof value === 'string' ? normalizeDecimalTyping(value) : value);
      } else if (field === 'unit_price') {
        updatedItem.unit_price = value === '' || value === null || value === undefined
          ? ''
          : (isEntryGuide
            ? roundPrice(parseFloat(value) || 0)
            : (typeof value === 'string'
              ? normalizeDecimalTyping(value)
              : Math.round((parseFloat(value) || 0) * 10000) / 10000));
      } else if (field === 'reference_unit_price') {
        updatedItem.reference_unit_price = value === '' || value === null || value === undefined
          ? ''
          : (typeof value === 'string' ? normalizeDecimalTyping(value) : value);
        if (value !== '' && value !== null && value !== undefined) {
          updatedItem._reference_manual = true;
        }
      } else if (field === 'has_igv' && isEntryGuide) {
        const newHasIgv = value === true || value === 'true';
        const oldHasIgv = currentItem.has_igv ?? true;
        updatedItem.has_igv = newHasIgv;
        if (oldHasIgv !== newHasIgv) {
          updatedItem.unit_price = adjustNetPriceOnHasIgvChange(currentItem.unit_price, {
            oldHasIgv,
            newHasIgv,
            pricesIncludeIgv,
            igvRate: ENTRY_GUIDE_IGV_RATE,
          });
        }
      } else {
        updatedItem[field] = value;
      }

      if (field === 'quantity' || field === 'unit_price' || field === 'reference_unit_price' || field === 'unit_of_measure' || (isEntryGuide && field === 'has_igv')) {
        updatedItem.total = resolveItemLineTotal(updatedItem);
        if (useReferenceLastPurchaseCost && (isExit || isTransfer || isReception)) {
          updatedItem = syncReferenceLineTotal(updatedItem);
        }
      }

      newItems[index] = updatedItem;
      
      // Validación en tiempo real: cantidad no puede exceder stock por condición (salida/traspaso)
      if (!relaxStockRestrictions && (isExit || isTransfer) && (field === 'quantity' || field === 'condition') && updatedItem.product_id) {
        const stockInfo = updatedItem.stock_info || (stockCostByProductRef.current[updatedItem.product_id] || stockCostByProductRef.current[String(updatedItem.product_id)]) ? buildStockInfoFromCache(stockCostByProductRef.current[updatedItem.product_id] || stockCostByProductRef.current[String(updatedItem.product_id)]) : null;
        if (stockInfo) {
          const qty = parseFloat(updatedItem.quantity) || 0;
          const cond = updatedItem.condition || 'good';
          const available = Number(stockInfo.stock_by_condition?.[cond]) || 0;
          setErrors(prev => {
            const next = { ...prev };
            if (qty > available) {
              const label = cond === 'good' ? 'Bueno' : cond === 'damaged' ? 'Dañado' : cond === 'defective' ? 'Defectuoso' : cond === 'reusable' ? 'Reutilizable' : cond === 'non_reusable' ? 'No reutilizable' : cond;
              next[`items.${index}.quantity`] = `La cantidad (${qty.toFixed(3)}) excede el stock disponible en condición "${label}" (${available.toFixed(3)})`;
            } else {
              delete next[`items.${index}.quantity`];
            }
            return next;
          });
        }
      }
      
      return { ...prev, items: newItems };
    });
  };

  // Eliminar item
  const handleRemoveItem = (index) => {
    const item = formData.items[index];
    if (itemHasReferenceLink(item)) {
      const confirmed = window.confirm(
        'Esta línea está vinculada a una referencia. Si la elimina, se perderá ese vínculo. ¿Continuar?',
      );
      if (!confirmed) return;
    }

    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  // Cargar guías de salida de consumos y saldo inicial para devolución
  const loadConsumptionExitGuides = async (supplierId, productId, warehouseId) => {
    setLoadingConsumptionGuides(true);
    try {
      // Cargar ambas en paralelo
      const [consumptionResponse, initialBalanceResponse] = await Promise.all([
        supplierId ? entryGuideService.getConsumptionExitGuides(supplierId, productId) : Promise.resolve({ success: true, data: [] }),
        entryGuideService.getInitialBalanceGuides(productId, warehouseId)
      ]);

      // Procesar guías de consumo
      if (consumptionResponse.success) {
        const guides = consumptionResponse.data || [];
        setConsumptionExitGuides(guides);
      } else {
        setConsumptionExitGuides([]);
      }

      // Procesar guías de saldo inicial
      if (initialBalanceResponse.success) {
        const guides = initialBalanceResponse.data || [];
        setInitialBalanceGuides(guides);
      } else {
        setInitialBalanceGuides([]);
      }

      // Siempre abrir el modal si hay al menos una guía disponible
      const hasConsumptionGuides = consumptionResponse.success && (consumptionResponse.data || []).length > 0;
      const hasInitialBalanceGuides = initialBalanceResponse.success && (initialBalanceResponse.data || []).length > 0;

      // Siempre abrir el modal: si hay guías se eligen; si no hay, el usuario puede continuar e ingresar datos manualmente
      setIsReturnModalOpen(true);
    } catch (error) {
      console.error('Error al cargar guías:', error);
      setToast({
        isOpen: true,
        message: 'Error al cargar las guías de referencia',
        type: 'error'
      });
      if (currentItemIndex !== null) {
        handleItemChange(currentItemIndex, 'kardex_event', 'compra');
      }
      setIsReturnModalOpen(false);
    } finally {
      setLoadingConsumptionGuides(false);
    }
  };

  // Confirmar devolución
  const handleConfirmReturn = () => {
    if (currentItemIndex !== null && selectedGuideIds.length > 0) {
      // Obtener las guías seleccionadas (de consumo y saldo inicial)
      const selectedConsumptionGuides = consumptionExitGuides.filter(guide => selectedGuideIds.includes(guide.id));
      const selectedInitialBalanceGuides = initialBalanceGuides.filter(guide => selectedGuideIds.includes(guide.id));
      const allSelectedGuides = [...selectedConsumptionGuides, ...selectedInitialBalanceGuides];
      
      if (allSelectedGuides.length > 0) {
        // Sumar cantidades y calcular promedios de costos (solo desde saldo disponible)
        let totalQuantity = 0;
        let totalAmount = 0;
        let unitOfMeasure = '';
        
        allSelectedGuides.forEach(guide => {
          if (guide.items && guide.items.length > 0) {
            guide.items.forEach(item => {
              const availableBalance = parseFloat(item.available_balance ?? item.quantity ?? 0);
              totalQuantity += availableBalance;
              const unitPrice = parseFloat(item.unit_price || 0);
              totalAmount += availableBalance * unitPrice;
              if (!unitOfMeasure && item.unit_of_measure) {
                unitOfMeasure = item.unit_of_measure;
              }
            });
          }
        });
        
        const averageUnitPrice = totalQuantity > 0 ? Math.round((totalAmount / totalQuantity) * 10000) / 10000 : 0;
        
        setFormData(prev => {
          const newItems = [...prev.items];
          const currentItem = newItems[currentItemIndex];
          const firstSelectedGuideId = allSelectedGuides[0]?.id || null;
          const firstCode = allSelectedGuides[0]?.code || '';
          
          // Si el saldo disponible total es 0 (solo referencia), mantener cantidad y costo actuales del ítem
          if (totalQuantity <= 0) {
            newItems[currentItemIndex] = {
              ...currentItem,
              kardex_event: 'devolucion',
              consumption_exit_guide_id: firstSelectedGuideId,
              consumption_exit_guide_code: firstCode,
              // quantity, unit_price, total, unit_of_measure se mantienen
            };
          } else {
            newItems[currentItemIndex] = {
              ...currentItem,
              kardex_event: 'devolucion',
              quantity: totalQuantity,
              unit_price: averageUnitPrice,
              total: totalAmount,
              unit_of_measure: unitOfMeasure || currentItem.unit_of_measure,
              consumption_exit_guide_id: firstSelectedGuideId,
              consumption_exit_guide_code: firstCode,
            };
          }
          
          return { ...prev, items: newItems };
        });
      }
      
      // Cambiar el evento de kardex a devolución
      handleItemChange(currentItemIndex, 'kardex_event', 'devolucion');
    }
    setIsReturnModalOpen(false);
    setSelectedGuideIds([]);
    setCurrentItemIndex(null);
    openedReturnModalFromSelectRef.current = false;
  };

  // Validar conversiones de unidades
  const validateConversions = () => {
    const conversionErrors = [];
    
    formData.items.forEach((item, index) => {
      if (!item.product_id || !item.unit_of_measure) return;
      
      const product = findProductById(item.product_id, item.product);
      if (!product) return;
      
      const productBaseUnit = product.unit_of_measure;
      const inputUnit = item.unit_of_measure;
      
      const unitsDiffer = inputUnit && productBaseUnit
        && !unitsEquivalent(inputUnit, productBaseUnit, units);

      if (!unitsDiffer) return;

      // Tasa personalizada del producto (ej. 1 cj = 12 u), aunque el ítem use otro código del catálogo (CAJA).
      if (productConversionsEnabled && itemHasCustomConversion({
        product,
        unitCode: inputUnit,
        productConversionsEnabled,
        units,
      })) {
        return;
      }

      // Catálogo global de conversiones
      const fromUnit = findUnitInCatalog(inputUnit, units);
      const toUnit = findUnitInCatalog(productBaseUnit, units);

      if (fromUnit && toUnit) {
        const conversion = conversions.find((c) =>
          c.from_unit_id === fromUnit.id
          && c.to_unit_id === toUnit.id
          && c.is_active === true,
        );

        if (!conversion) {
          const productName = product.name || `Producto ID ${item.product_id}`;
          const fromUnitName = fromUnit.name || inputUnit;
          const toUnitName = toUnit.name || productBaseUnit;
          conversionErrors.push({
            index,
            productName,
            fromUnit: fromUnitName,
            toUnit: toUnitName,
            message: `Item ${index + 1} (${productName}): No se encontró una tasa de conversión activa de ${fromUnitName} (${inputUnit}) a ${toUnitName} (${productBaseUnit}). Configure la tasa personalizada en el producto o la conversión en Configuración > Conversiones de Unidades.`,
          });
        }
      }
    });
    
    return conversionErrors;
  };

  const validate = () => {
    const newErrors = {};

    // Validar stock disponible para guías de salida
    if (isExit && !relaxStockRestrictions) {
      formData.items.forEach((item, index) => {
        const stockInfo = item.stock_info || (item.product_id && (stockCostByProduct[item.product_id] || stockCostByProduct[String(item.product_id)]) ? buildStockInfoFromCache(stockCostByProduct[item.product_id] || stockCostByProduct[String(item.product_id)]) : null);
        if (item.product_id && stockInfo) {
          const quantity = parseFloat(item.quantity) || 0;
          const condition = item.condition || 'good';
          const availableStock = Number(stockInfo.stock_by_condition?.[condition]) || 0;
          
          if (quantity > availableStock) {
            const conditionLabel = condition === 'good' ? 'Bueno' : condition === 'damaged' ? 'Dañado' : condition === 'defective' ? 'Defectuoso' : condition === 'reusable' ? 'Reutilizable' : condition === 'non_reusable' ? 'No reutilizable' : condition;
            newErrors[`items.${index}.quantity`] = `La cantidad (${quantity.toFixed(3)}) excede el stock disponible en condición "${conditionLabel}" (${availableStock.toFixed(3)})`;
          }
        }
      });
    }

    if (!formData.warehouse_id) newErrors.warehouse_id = 'El almacén es requerido';
    if (isTransfer && !formData.destination_warehouse_id) {
      newErrors.destination_warehouse_id = 'El almacén destino es requerido';
    }
    if (isTransfer && formData.destination_warehouse_id && String(formData.destination_warehouse_id) === String(formData.warehouse_id)) {
      newErrors.destination_warehouse_id = 'El almacén destino debe ser diferente al de origen';
    }
    // La entidad solo es requerida para guías de entrada y salida, no para iniciales, traspasos o recepciones
    if (!isInitial && !isTransfer && !isReception && !formData.supplier_id) {
      newErrors.supplier_id = 'La entidad es requerida';
    }
    const issueDate = parseFlexibleDateInput(formData.issue_date) || formData.issue_date;
    const entryDate = parseFlexibleDateInput(formData.entry_date) || formData.entry_date;
    if (!issueDate) {
      newErrors.issue_date = formData.issue_date
        ? 'Fecha de emisión inválida (use DD/MM/AAAA)'
        : 'La fecha de emisión es requerida';
    }
    if (!entryDate) {
      newErrors.entry_date = formData.entry_date
        ? 'Fecha de ingreso inválida (use DD/MM/AAAA)'
        : 'La fecha de ingreso es requerida';
    }
    if (issueDate && entryDate && entryDate < issueDate) {
      newErrors.entry_date = 'La fecha de ingreso no puede ser anterior a la de emisión';
    }
    if (formData.entry_time?.trim()) {
      const entryTime = parseFlexibleTimeInput(formData.entry_time) || formData.entry_time;
      if (!/^\d{2}:\d{2}$/.test(entryTime || '')) {
        newErrors.entry_time = 'Hora inválida (use HH:mm)';
      }
    }

    if (formData.items.length === 0) {
      newErrors.items = 'Debe agregar al menos un item a la guía';
    }

    formData.items.forEach((item, index) => {
      if (!item.product_id) {
        newErrors[`items.${index}.product_id`] = 'El producto es requerido';
      }
      if (!item.kardex_event && !isTransfer && !isReception) {
        newErrors[`items.${index}.kardex_event`] = 'El evento de kardex es requerido';
      }
      if (parseDecimalNumber(item.quantity) <= 0) {
        newErrors[`items.${index}.quantity`] = 'La cantidad debe ser mayor a 0';
      }
      
      // Validar cantidad para guías con referencia: debe ser mayor a 0 (puede ser 1)
      if (
        (formData.document_id || entryGuide?.document_id || referenceSnapshot?.document_id)
      ) {
        const quantity = parseFloat(item.quantity) || 0;
        if (quantity <= 0) {
          newErrors[`items.${index}.quantity`] = 'La cantidad debe ser mayor a 0 cuando la guía tiene referencia a un documento';
        }
      }
      if (useReferenceLastPurchaseCost && (isExit || isTransfer || isReception)) {
        if (parseDecimalNumber(item.reference_unit_price) <= 0) {
          newErrors[`items.${index}.unit_price`] = 'Ingrese un costo unitario referencial mayor a 0';
        }
      } else if (isExit && !relaxStockRestrictions) {
        if (item.stock_info != null && (parseFloat(item.unit_price) || 0) <= 0) {
          newErrors[`items.${index}.unit_price`] = 'El costo unitario debe ser mayor a 0';
        }
      } else if (isExit && relaxStockRestrictions) {
        if (parseDecimalNumber(item.unit_price) <= 0) {
          newErrors[`items.${index}.unit_price`] = 'El costo unitario debe ser mayor a 0';
        }
      } else {
        if (!item.unit_price || item.unit_price <= 0) {
          newErrors[`items.${index}.unit_price`] = 'El costo unitario debe ser mayor a 0';
        }
      }
      if (!item.condition) {
        newErrors[`items.${index}.condition`] = 'La condición es requerida';
      }

      // Validar stock disponible para guías de salida y traspasos (por condición: Bueno, Reutilizable, No reutilizable, etc.)
      if (!relaxStockRestrictions && (isExit || isTransfer) && item.product_id) {
        const stockInfo = item.stock_info || (stockCostByProduct[item.product_id] || stockCostByProduct[String(item.product_id)]) ? buildStockInfoFromCache(stockCostByProduct[item.product_id] || stockCostByProduct[String(item.product_id)]) : null;
        if (stockInfo) {
          const quantity = parseFloat(item.quantity) || 0;
          const condition = item.condition || 'good';
          const availableStock = Number(stockInfo.stock_by_condition?.[condition]) || 0;
          
          if (quantity > availableStock) {
            const conditionLabel = condition === 'good' ? 'Bueno' : condition === 'damaged' ? 'Dañado' : condition === 'defective' ? 'Defectuoso' : condition === 'reusable' ? 'Reutilizable' : condition === 'non_reusable' ? 'No reutilizable' : condition;
            newErrors[`items.${index}.quantity`] = `La cantidad (${quantity.toFixed(3)}) excede el stock disponible en condición "${conditionLabel}" (${availableStock.toFixed(3)})`;
          }
        }
      }
      
      // Validar cantidad para recepciones: no puede exceder la cantidad original del traspaso
      if (isReception && item.transfer_guide_item_id && item.original_quantity) {
        const quantity = parseFloat(item.quantity) || 0;
        const originalQuantity = parseFloat(item.original_quantity) || 0;
        
        if (quantity > originalQuantity) {
          newErrors[`items.${index}.quantity`] = `La cantidad (${quantity.toFixed(3)}) no puede ser mayor a la cantidad original del traspaso (${originalQuantity.toFixed(3)})`;
        }
      }
    });

    // Validar conversiones
    const conversionErrors = validateConversions();
    if (conversionErrors.length > 0) {
      newErrors.conversions = conversionErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const productIds = formData.items.map((item) => item.product_id).filter(Boolean);
    if (productIds.length) {
      await ensureByIds(productIds);
    }

    if (!validate()) {
      // Si hay errores de conversión, mostrar mensaje específico
      if (errors.conversions && errors.conversions.length > 0) {
        const errorMessages = errors.conversions.map(err => err.message).join('\n');
        setToast({
          isOpen: true,
          message: `Error de conversión de unidades:\n${errorMessages}`,
          type: 'error'
        });
      } else {
        setToast({
          isOpen: true,
          message: 'Por favor, corrige los errores en el formulario',
          type: 'error'
        });
      }
      return;
    }

    if (entryGuide?.id && referenceSnapshot && hasEntryGuideReference(referenceSnapshot)) {
      const analysis = analyzeEntryGuideReferenceChanges(formData, referenceSnapshot);
      if (!analysis.safeEdit) {
        const confirmed = window.confirm(buildReferenceLossConfirmMessage(analysis, referenceSnapshot));
        if (!confirmed) return;
      }
    }

    setLoading(true);

    try {
      const referenceIds = resolveSubmitReferenceIds(formData, referenceSnapshot);

      // Eventos de bitácora para guía de salida (un evento por ítem con machinery_register_enabled)
      let machineryEventsPayload = [];
      if (isExit) {
        const itemIndexesWithPending = new Set(pendingMachineryEvents.map((e) => e.itemIndex));
        const itemIndexesWithLoaded = new Set((loadedMachineryEventsByItem || []).map((e) => e.itemIndex));
        const allIndexes = new Set([...itemIndexesWithPending, ...itemIndexesWithLoaded]);
        for (const itemIndex of allIndexes) {
          const pending = pendingMachineryEvents.find((e) => e.itemIndex === itemIndex);
          if (pending) {
            const { itemIndex: _i, displaySummary: _s, ...ev } = pending;
            machineryEventsPayload.push({ ...ev, item_index: _i });
          } else {
            const form = lastEventFormByItem[itemIndex];
            if (form) {
              machineryEventsPayload.push({
                event_date: form.event_date || null,
                event_time: form.event_time || null,
                machine_id: form.machine_id || null,
                vehicle_id: form.vehicle_id || null,
                event_type: form.event_type || null,
                operator: form.operator || null,
                hours: form.hours !== '' && form.hours != null ? form.hours : null,
                kilometers: form.kilometers !== '' && form.kilometers != null ? form.kilometers : null,
                project_id: form.project_id && form.project_id !== '' ? form.project_id : null,
                notes: form.notes || null,
                item_index: itemIndex,
              });
            }
          }
        }
      }

      const submitData = {
        guide_type: isInitial ? 'inicial' : (isExit ? 'salida' : (isTransfer ? 'traspaso' : (isReception ? 'recepcion' : 'entrada'))),
        warehouse_id: parseInt(formData.warehouse_id),
        destination_warehouse_id: (isTransfer && formData.destination_warehouse_id && formData.destination_warehouse_id !== '') ? parseInt(formData.destination_warehouse_id) : null,
        transfer_guide_id: (isReception && formData.transfer_guide_id && formData.transfer_guide_id !== '') ? parseInt(formData.transfer_guide_id) : null,
        supplier_id: (formData.supplier_id && formData.supplier_id !== '') ? parseInt(formData.supplier_id) : null,
        issue_date: parseFlexibleDateInput(formData.issue_date) || formData.issue_date,
        entry_date: parseFlexibleDateInput(formData.entry_date) || formData.entry_date,
        entry_time: (() => {
          if (!formData.entry_time || formData.entry_time.trim() === '') return null;
          const parsed = parseFlexibleTimeInput(formData.entry_time) || formData.entry_time;
          return parsed.slice(0, 5);
        })(),
        received_by: formData.received_by || null,
        notes: formData.notes || null,
        currency: (!isInitial && !isTransfer && !isReception) ? (formData.currency || 'PEN') : null,
        purchase_order_id: referenceIds.purchase_order_id,
        requirement_id: referenceIds.requirement_id,
        document_id: referenceIds.document_id,
        ...(isExit ? { machinery_events: machineryEventsPayload } : {}),
        items: formData.items.map((item) => {
          const lineItem = (useReferenceLastPurchaseCost && (isExit || isTransfer || isReception))
            ? syncReferenceLineTotal(item)
            : item;
          return {
          id: lineItem.id || null,
          product_id: lineItem.product_id ? parseInt(lineItem.product_id) : null,
          description: lineItem.description || null,
          project_id: showProjectField && lineItem.project_id && lineItem.project_id !== ''
            ? parseInt(lineItem.project_id)
            : null,
          kardex_event: lineItem.kardex_event || (isInitial ? 'saldo_inicial' : (isExit ? 'venta' : (isTransfer ? 'traspaso' : (isReception ? 'recepcion' : 'compra')))),
          quantity: parseFloat(lineItem.quantity),
          unit_price: Math.round((parseFloat(lineItem.unit_price) || 0) * 10000) / 10000,
          reference_unit_price: lineItem.reference_unit_price != null && lineItem.reference_unit_price !== ''
            ? Math.round((parseFloat(lineItem.reference_unit_price) || 0) * 10000) / 10000
            : null,
          reference_total: lineItem.reference_total != null && lineItem.reference_total !== ''
            ? parseFloat(lineItem.reference_total)
            : null,
          total: lineItem.total !== undefined && lineItem.total !== null
            ? parseFloat(lineItem.total)
            : resolveItemLineTotal(lineItem),
          unit_of_measure: lineItem.unit_of_measure || null,
          condition: lineItem.condition || 'good',
          purchase_order_item_id: lineItem.purchase_order_item_id || null,
          requirement_item_id: lineItem.requirement_item_id || null,
          document_item_id: lineItem.document_item_id || null,
          consumption_exit_guide_id: (lineItem.consumption_exit_guide_id && lineItem.consumption_exit_guide_id !== '') ? parseInt(lineItem.consumption_exit_guide_id) : null,
        };
        }),
      };
      
      // Log para debugging
      console.log('Enviando datos para actualizar:', {
        guide_id: entryGuide?.id,
        guide_type: submitData.guide_type,
        currency: submitData.currency,
        items_count: submitData.items.length,
        first_item: submitData.items[0]
      });

      let response;
      if (entryGuide && entryGuide.id) {
        response = await entryGuideService.updateEntryGuide(entryGuide.id, submitData);
        setToast({
          isOpen: true,
          message: formatGuideSaveMessage('Guía de entrada actualizada exitosamente', response),
          type: 'success'
        });
      } else {
        response = await entryGuideService.createEntryGuide(submitData);
        setToast({
          isOpen: true,
          message: formatGuideSaveMessage('Guía de entrada creada exitosamente', response),
          type: 'success'
        });
      }

      if (response.success) {
        setTimeout(() => {
          if (onSuccess) onSuccess(response.data);
          if (onClose) onClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Error al guardar guía de entrada:', error);
      
      // Manejar errores de conversión del backend
      if (error.response?.data?.conversion_errors && error.response.data.conversion_errors.length > 0) {
        const errorMessages = error.response.data.conversion_errors.join('\n');
        setToast({
          isOpen: true,
          message: `Error de conversión de unidades:\n${errorMessages}`,
          type: 'error'
        });
        
        // Agregar errores al estado para mostrarlos en el formulario
        setErrors(prev => ({
          ...prev,
          conversions: error.response.data.conversion_errors.map((msg, idx) => ({
            index: idx,
            message: msg
          }))
        }));
      } else {
        // Mostrar errores de validación del backend
        const errorMessage = error.response?.data?.message || error.message || 'Error al guardar la guía de entrada';
        const validationErrors = error.response?.data?.errors;
        
        let fullMessage = errorMessage;
        if (validationErrors) {
          const errorDetails = Object.entries(validationErrors)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('\n');
          fullMessage = `${errorMessage}\n\n${errorDetails}`;
          console.error('Errores de validación:', validationErrors);
        }
        
        setToast({
          isOpen: true,
          message: fullMessage,
          type: 'error'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isPageMode && !isOpen) return null;

  const handleFormKeyDown = (e) => {
    handlePreventEnterFormSubmit(e, {
      onEnterInZone:
        quickProductLineEnabled && !(entryGuide && entryGuide.document_id)
          ? () => handleQuickAddProduct()
          : undefined,
    });
  };

  const formContent = (
    <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className={isPageMode ? "p-8" : "px-6 py-4"}>
      {errors.submit && (
        <div className="mb-6 bg-red-50 text-red-700 p-4 rounded-lg text-sm border border-red-200 flex items-center gap-2">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {errors.submit}
        </div>
      )}

      {errors.conversions && errors.conversions.length > 0 && (
        <div className="mb-6 bg-red-50 text-red-700 p-4 rounded-lg text-sm border border-red-200">
          <div className="flex items-center gap-2 mb-3">
            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <strong>Error de conversión de unidades:</strong>
          </div>
          <ul className="list-disc list-inside space-y-2 ml-7">
            {errors.conversions.map((error, idx) => (
              <li key={idx} className="text-sm">
                {error.message || error}
              </li>
            ))}
          </ul>
          <div className="mt-3 pt-3 border-t border-red-200 text-xs text-red-600">
            <p>Por favor, configure la <strong>tasa personalizada en el producto</strong> o la conversión en <strong>Configuración &gt; Conversiones de Unidades</strong> antes de guardar.</p>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Número/Código de guía */}
        {codePreview && (
          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-200 rounded-xl p-5 shadow-sm">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
              </svg>
              {entryGuide ? 'N° de guía' : 'Código (Generado automáticamente)'}
            </label>
            <div className="text-2xl font-mono font-bold text-teal-700 mb-1">{codePreview}</div>
            {!entryGuide && (
              <p className="text-xs text-gray-600 mt-2">
                Este código se generará automáticamente al guardar la guía
              </p>
            )}
          </div>
        )}

        {/* Información Básica */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex justify-between items-center mb-5 pb-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Información Básica
            </h3>
            {!isInitial && !isExit && !isTransfer && !isReception && !(entryGuide && entryGuide.document_id) && (
              <button
                type="button"
                onClick={openOrderSelectorModal}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Seleccionar referencias
              </button>
            )}
            {isReception && (
              <div className="flex items-center gap-2">
              {isReceptionConsumeAutomationActive({
                receptionConsumeButtonEnabled,
                receptionConsumeWarehouseIds,
                warehouseId: formData.warehouse_id || warehouseId,
              }) && entryGuide?.id && !receptionConsumeTrace && (
                <ReceptionConsumeButton
                  onClick={() => setIsConsumeModalOpen(true)}
                />
              )}
              {receptionConsumeTrace && (
                <span
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800"
                  title="Esta recepción ya fue consumida"
                >
                  Consumido{receptionConsumeTrace.code ? ` → ${receptionConsumeTrace.code}` : ''}
                </span>
              )}
              <button
                type="button"
                onClick={async () => {
                  setIsTransferGuideSelectorModalOpen(true);
                  // Cargar guías de traspaso pendientes
                  try {
                    const selectedWarehouseId = formData.warehouse_id || warehouseId;
                    console.log('Cargando guías de traspaso pendientes para warehouse_id:', selectedWarehouseId);
                    console.log('formData.warehouse_id:', formData.warehouse_id);
                    console.log('warehouseId prop:', warehouseId);
                    
                    if (!selectedWarehouseId) {
                      setToast({
                        isOpen: true,
                        message: 'Por favor, seleccione primero el almacén de recepción',
                        type: 'warning'
                      });
                      setIsTransferGuideSelectorModalOpen(false);
                      return;
                    }
                    
                    const response = await entryGuideService.getPendingTransferGuides(selectedWarehouseId);
                    console.log('Respuesta de guías de traspaso pendientes:', response);
                    console.log('Response success:', response.success);
                    console.log('Response data:', response.data);
                    console.log('Cantidad de guías:', response.data?.length || 0);
                    
                    if (response.success) {
                      const guides = response.data || [];
                      console.log('Guías encontradas:', guides.length, guides);
                      setAvailableTransferGuides(guides);
                      
                      if (guides.length === 0) {
                        setToast({
                          isOpen: true,
                          message: 'No hay guías de traspaso pendientes para este almacén',
                          type: 'info'
                        });
                      }
                    } else {
                      console.error('Error en respuesta:', response.message);
                      setToast({
                        isOpen: true,
                        message: response.message || 'Error al cargar las guías de traspaso',
                        type: 'error'
                      });
                    }
                  } catch (error) {
                    console.error('Error al cargar guías de traspaso:', error);
                    console.error('Error completo:', error.response || error);
                    setToast({
                      isOpen: true,
                      message: 'Error al cargar las guías de traspaso: ' + (error.message || 'Error desconocido'),
                      type: 'error'
                    });
                  }
                }}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Seleccionar Guía de Traspaso
              </button>
              </div>
            )}
          </div>
          <div className={`grid grid-cols-1 ${!isInitial && !isTransfer && !isReception ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4`}>
            {/* Almacén de Origen - Solo para recepciones */}
            {isReception && selectedTransferGuide && (
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Almacén de Origen <span className="text-xs text-gray-500 font-normal">(desde traspaso)</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={selectedTransferGuide.warehouse ? `${selectedTransferGuide.warehouse.code} - ${selectedTransferGuide.warehouse.name}` : 'N/A'}
                    readOnly
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed"
                  />
                  <div className="absolute inset-0 bg-gray-50 bg-opacity-50 rounded-lg pointer-events-none flex items-center justify-end pr-3">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>
            )}

            {/* Almacén */}
            {!isReception && (
              <div className={!isInitial && !isTransfer ? 'md:col-span-4' : 'md:col-span-3'}>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="warehouse_id" className="block text-sm font-medium text-gray-700">
                    {isTransfer ? 'Almacén Origen' : 'Almacén Destino'} <span className="text-red-500">*</span>
                  </label>
                </div>
                <select
                  id="warehouse_id"
                  name="warehouse_id"
                  value={formData.warehouse_id}
                  onChange={handleChange}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                    errors.warehouse_id ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                  }`}
                  disabled={!!warehouseId}
                >
                  <option value="">Seleccionar almacén</option>
                  {warehouses.map(warehouse => (
                    <option key={warehouse.id} value={warehouse.id}>
                      {warehouse.code} - {warehouse.name}
                    </option>
                  ))}
                </select>
                {errors.warehouse_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.warehouse_id}</p>
                )}
                {(isExit || isTransfer) && loadingStockCostBulk && formData.warehouse_id && (
                  <p className="mt-1 text-sm text-gray-500">Cargando costos por almacén...</p>
                )}
              </div>
            )}

            {/* Almacén de Recepción - Solo para recepciones */}
            {isReception && (
              <div className="md:col-span-3">
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="warehouse_id" className="block text-sm font-medium text-gray-700">
                    Almacén de Recepción <span className="text-red-500">*</span>
                    {formData.transfer_guide_id && (
                      <span className="ml-2 text-xs text-gray-500 font-normal">(desde traspaso)</span>
                    )}
                  </label>
                </div>
                <div className="relative">
                  <select
                    id="warehouse_id"
                    name="warehouse_id"
                    value={formData.warehouse_id}
                    onChange={handleChange}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                      errors.warehouse_id ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                    } ${formData.transfer_guide_id ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                    disabled={!!warehouseId || (formData.transfer_guide_id)}
                  >
                    <option value="">Seleccionar almacén</option>
                    {warehouses.map(warehouse => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.code} - {warehouse.name}
                      </option>
                    ))}
                  </select>
                  {formData.transfer_guide_id && (
                    <div className="absolute inset-0 bg-gray-50 bg-opacity-50 rounded-lg pointer-events-none flex items-center justify-end pr-3">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  )}
                </div>
                {errors.warehouse_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.warehouse_id}</p>
                )}
              </div>
            )}

            {/* Almacén Destino - Solo para traspasos */}
            {isTransfer && (
              <div className="md:col-span-3">
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="destination_warehouse_id" className="block text-sm font-medium text-gray-700">
                    Almacén Destino <span className="text-red-500">*</span>
                  </label>
                </div>
                <select
                  id="destination_warehouse_id"
                  name="destination_warehouse_id"
                  value={formData.destination_warehouse_id}
                  onChange={handleChange}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                    errors.destination_warehouse_id ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                  }`}
                >
                  <option value="">Seleccionar almacén destino</option>
                  {warehouses
                    .filter((warehouse) => String(warehouse.id) !== String(formData.warehouse_id))
                    .map((warehouse) => (
                    <option key={warehouse.id} value={warehouse.id}>
                      {warehouse.code} - {warehouse.name}
                    </option>
                  ))}
                </select>
                {errors.destination_warehouse_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.destination_warehouse_id}</p>
                )}
              </div>
            )}

            <div className={!isInitial && !isTransfer && !isReception ? 'md:col-span-4' : 'md:col-span-3'}>
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="supplier_id" className="block text-sm font-medium text-gray-700">
                  Entidad {!isReception && <span className="text-red-500">*</span>}
                  {isReception && formData.transfer_guide_id && (
                    <span className="ml-2 text-xs text-gray-500 font-normal">(desde traspaso)</span>
                  )}
                </label>
                {!isReception && (
                  <button
                    type="button"
                    onClick={() => setIsCreateSupplierModalOpen(true)}
                    className="text-xs px-3 py-1 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
                  >
                    + Nuevo
                  </button>
                )}
              </div>
              <div className="relative">
                <SupplierSelector
                  value={formData.supplier_id}
                  onChange={(value) => handleChange({ target: { name: 'supplier_id', value } })}
                  suppliers={suppliers}
                  error={errors.supplier_id}
                  disabled={isReception && formData.transfer_guide_id} // Deshabilitar si viene de traspaso
                />
                {isReception && formData.transfer_guide_id && (
                  <div className="absolute inset-0 bg-gray-50 bg-opacity-50 rounded-lg pointer-events-none flex items-center justify-end pr-3">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                )}
              </div>
            </div>

            {/* Fechas, Hora y Moneda en la misma fila para guías de entrada y salida */}
            {!isInitial && !isTransfer && !isReception ? (
              <>
                <GuideDateField
                  id="issue_date"
                  name="issue_date"
                  label="Fecha de Emisión"
                  value={formData.issue_date}
                  onChange={handleChange}
                  error={errors.issue_date}
                  required
                  onValidationError={handleDateTimeValidationError}
                />

                <GuideDateField
                  id="entry_date"
                  name="entry_date"
                  label="Fecha de Ingreso"
                  value={formData.entry_date}
                  onChange={handleChange}
                  error={errors.entry_date}
                  required
                  min={formData.issue_date || undefined}
                  onValidationError={handleDateTimeValidationError}
                />

                <GuideTimeField
                  id="entry_time"
                  name="entry_time"
                  label="Hora de Ingreso"
                  value={formData.entry_time}
                  onChange={handleChange}
                  error={errors.entry_time}
                  onValidationError={handleDateTimeValidationError}
                />

                <div>
                  <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-2">
                    Moneda <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                      errors.currency ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                    }`}
                  >
                    <option value="PEN">Soles (S/)</option>
                    <option value="USD">Dólares (US$)</option>
                  </select>
                  {errors.currency && (
                    <p className="mt-1 text-sm text-red-600">{errors.currency}</p>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="md:col-span-1">
                  <GuideDateField
                    id="issue_date"
                    name="issue_date"
                    label="Fecha de Emisión"
                    value={formData.issue_date}
                    onChange={handleChange}
                    error={errors.issue_date}
                    required
                    onValidationError={handleDateTimeValidationError}
                  />
                </div>

                <div className="md:col-span-1">
                  <GuideDateField
                    id="entry_date"
                    name="entry_date"
                    label="Fecha de Ingreso"
                    value={formData.entry_date}
                    onChange={handleChange}
                    error={errors.entry_date}
                    required
                    min={formData.issue_date || undefined}
                    onValidationError={handleDateTimeValidationError}
                  />
                </div>

                <div className="md:col-span-1">
                  <GuideTimeField
                    id="entry_time"
                    name="entry_time"
                    label="Hora de Ingreso"
                    value={formData.entry_time}
                    onChange={handleChange}
                    error={errors.entry_time}
                    onValidationError={handleDateTimeValidationError}
                  />
                </div>
              </>
            )}

            <div className={!isInitial && !isTransfer && !isReception ? 'md:col-span-4' : 'md:col-span-3'}>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                Observaciones
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                placeholder="Observaciones adicionales..."
              />
            </div>
          </div>
        </div>

        {/* Referencias seleccionadas (edición o creación al jalar OC/Documento) */}
        {entryGuide?.id && isEntryGuide && referenceSnapshot && hasEntryGuideReference(referenceSnapshot) ? (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
            {buildReferenceEditInfoMessage()}
          </div>
        ) : null}
        {(relatedPurchaseOrder || relatedDocument || relatedRequirement || (referenceSnapshot?.linked_invoice_documents?.length > 0)) && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h3 className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Documentos Relacionados
              </h3>
              {!entryGuide && (
                <button
                  type="button"
                  onClick={handleClearReference}
                  className="inline-flex items-center justify-center rounded-md border border-red-200 bg-red-50 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
                  title="Quitar referencia y eliminar productos jalados"
                >
                  ✕ Quitar referencia
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              {relatedPurchaseOrder && (
                <div className="flex-1 min-w-[280px] bg-white border border-gray-200 rounded-md p-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-gray-500">Orden de Compra</span>
                    <span className="text-xs font-mono text-gray-600">{relatedPurchaseOrder.code}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    <div>
                      <span className="text-gray-500">Entidad: </span>
                      <span className="text-gray-700">{relatedPurchaseOrder.supplier?.name || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Fecha: </span>
                      <span className="text-gray-700">
                        {(relatedPurchaseOrder.order_date || relatedPurchaseOrder.issue_date)
                          ? formatDateLocaleLima(relatedPurchaseOrder.order_date || relatedPurchaseOrder.issue_date)
                          : 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Estado: </span>
                      {(() => {
                        const f = getPurchaseOrderFulfillmentLabel(relatedPurchaseOrder);
                        return (
                          <span className={`px-1.5 py-0.5 text-xs rounded ${f.className}`}>
                            {f.label}
                          </span>
                        );
                      })()}
                    </div>
                      {relatedPurchaseOrder.total_amount && (
                      <div>
                        <span className="text-gray-500">Total: </span>
                        <span className="text-gray-700 font-medium">
                          {relatedPurchaseOrder.currency || 'PEN'} {parseFloat(relatedPurchaseOrder.total_amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {relatedRequirement && (
                <div className="flex-1 min-w-[280px] bg-white border border-teal-200 rounded-md p-2.5 bg-teal-50/30">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-teal-800">Requerimiento</span>
                    <span className="text-xs font-mono text-teal-700">{relatedRequirement.code}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700">
                    <div>
                      <span className="text-gray-500">Fecha: </span>
                      {relatedRequirement.request_date
                        ? formatDateLocaleLima(relatedRequirement.request_date)
                        : '—'}
                    </div>
                    <div>
                      <span className="text-gray-500">Avance: </span>
                      {relatedRequirement.fulfillment_percent ?? 0}%
                    </div>
                  </div>
                </div>
              )}

              {relatedDocument && (
                <div className="flex-1 min-w-[280px] bg-white border border-gray-200 rounded-md p-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-gray-500">Documento</span>
                    <span className="text-xs font-mono text-gray-600">{relatedDocument.code}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    <div>
                      <span className="text-gray-500">Tipo: </span>
                      <span className="text-gray-700 capitalize">{relatedDocument.document_type || 'N/A'}</span>
                    </div>
                    {(relatedDocument.series || relatedDocument.number) && (
                      <div>
                        <span className="text-gray-500">Serie/Número: </span>
                        <span className="text-gray-700 font-mono">
                          {relatedDocument.series && relatedDocument.number 
                            ? `${relatedDocument.series}-${relatedDocument.number}`
                            : relatedDocument.series || relatedDocument.number || 'N/A'}
                        </span>
                      </div>
                    )}
                    <div>
                      <span className="text-gray-500">Entidad: </span>
                      <span className="text-gray-700">{relatedDocument.supplier?.name || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Fecha: </span>
                      <span className="text-gray-700">{relatedDocument.issue_date ? formatDateLocaleLima(relatedDocument.issue_date) : 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Estado: </span>
                      <span className={`px-1.5 py-0.5 text-xs rounded ${
                        relatedDocument.status === 'issued' 
                          ? 'bg-green-50 text-green-700' 
                          : relatedDocument.status === 'cancelled'
                          ? 'bg-red-50 text-red-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {relatedDocument.status === 'issued' ? 'Emitido' : relatedDocument.status === 'cancelled' ? 'Cancelado' : relatedDocument.status}
                      </span>
                    </div>
                      {relatedDocument.total_amount && (
                      <div>
                        <span className="text-gray-500">Total: </span>
                        <span className="text-gray-700 font-medium">
                          {relatedDocument.currency || 'PEN'} {parseFloat(relatedDocument.total_amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(referenceSnapshot?.linked_invoice_documents || []).map((doc) => (
                <div key={doc.id || doc.code} className="flex-1 min-w-[280px] bg-white border border-amber-200 rounded-md p-2.5 bg-amber-50/40">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-medium text-amber-900">Factura vinculada</span>
                    <span className="text-xs font-mono text-amber-800">{doc.code}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700">
                    <div>
                      <span className="text-gray-500">Referencia: </span>
                      {doc.reference_label || doc.code}
                    </div>
                    {doc.invoiced_amount != null ? (
                      <div>
                        <span className="text-gray-500">Facturado: </span>
                        S/ {parseFloat(doc.invoiced_amount).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Items de la Guía */}
        <div className="bg-white border border-gray-200 rounded-xl p-3 sm:p-6 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4 pb-3 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Items de la Guía
            </h3>
            {!(entryGuide && entryGuide.document_id) && (
              <div className="flex flex-wrap items-center gap-2">
                {!quickProductLineEnabled && (
                  <button
                    type="button"
                    onClick={handleAddProduct}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
                    title="Atajo: Ctrl+Alt+P"
                  >
                    + Agregar Producto <kbd className="ml-1 text-[10px] opacity-90">Ctrl+Alt+P</kbd>
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleDownloadGuideItemsTemplate}
                  className="px-3 py-2 border border-gray-300 bg-white text-gray-800 rounded-lg hover:bg-gray-50 text-sm font-medium"
                  title="Incluye la hoja «Instrucciones» con la leyenda de columnas, validación y eventos de kardex"
                >
                  Descargar plantilla
                </button>
                <input
                  ref={guideItemsImportFileRef}
                  type="file"
                  accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  onChange={handleGuideItemsImportFile}
                />
                <button
                  type="button"
                  onClick={handlePickGuideItemsImport}
                  disabled={importingItems}
                  className="px-3 py-2 border border-teal-300 bg-teal-50 text-teal-900 rounded-lg hover:bg-teal-100 text-sm font-medium disabled:opacity-50"
                  title="Misma estructura que la plantilla; filas con datos inválidos se rechazan (consulte Instrucciones en el Excel descargado)"
                >
                  {importingItems ? 'Importando…' : 'Importar Excel'}
                </button>
              </div>
            )}
          </div>

          {errors.items && (
            <div className="mb-4 bg-red-50 text-red-700 p-3 rounded-lg text-sm">
              {errors.items}
            </div>
          )}

          {isEntryGuide && (
            <div className="mb-4">
              <PricesIncludeIgvToggle
                value={pricesIncludeIgv}
                onChange={setPricesIncludeIgv}
                igvRate={ENTRY_GUIDE_IGV_RATE}
              />
            </div>
          )}

          {showProductLineDefaults && (
            <ProductLineDefaultsBar
              productsProjectEnabled={productsProjectEnabled && showProjectField}
              productsIgvEnabled={productsIgvEnabled && isEntryGuide}
              projects={projects}
              defaultProjectId={defaultLineProjectId}
              onDefaultProjectIdChange={setDefaultLineProjectId}
              defaultHasIgv={defaultLineHasIgv}
              onDefaultHasIgvChange={setDefaultLineHasIgv}
              className="mb-4"
            />
          )}

          {!(entryGuide && entryGuide.document_id) && quickProductLineEnabled && (
            <QuickProductLineBar onAdd={handleQuickAddProduct} error={stagingError}>
              <LineItemField grow label="Producto" required className={GIC.product}>
                <ProductSelector
                  ref={stagingProductSelectorRef}
                  value={stagingItem.product_id}
                  onChange={(value) => handleStagingChange('product_id', value)}
                  {...productSelectorAsyncProps}
                  compact
                  productStockMap={(isExit || isTransfer) ? stockCostByProduct : undefined}
                />
              </LineItemField>
              {!(isTransfer || isReception) && (
                <LineItemField label="Kardex" required className={GIC.kardex}>
                  <select
                    value={stagingItem.kardex_event || (isInitial ? 'saldo_inicial' : (isExit ? 'venta' : 'compra'))}
                    onChange={(e) => handleStagingChange('kardex_event', e.target.value)}
                    className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                    disabled={isInitial}
                  >
                    {isInitial ? (
                      <option value="saldo_inicial">Saldo Inicial</option>
                    ) : isExit ? (
                      <>
                        <option value="venta">Venta</option>
                        <option value="salida_produccion">Salida a producción</option>
                        <option value="descuento_concedido">Descuento Concedido</option>
                        <option value="consumo">Consumo</option>
                        <option value="transferencia_gratuita">Transferencia Gratuita</option>
                        <option value="descuento_con_mercaderia">Descuento con Mercadería</option>
                      </>
                    ) : (
                      <>
                        <option value="compra">Compra</option>
                        <option value="entrada_produccion">Entrada de producción</option>
                        <option value="entrada_producto_terminado">Entrada producto terminado</option>
                        <option value="devolucion">Devolución</option>
                        <option value="transferencia_gratuita">Transferencia Gratuita</option>
                        <option value="descuento_obtenido">Descuento Obtenido</option>
                        <option value="descuento_con_mercaderia">Descuento con Mercadería</option>
                      </>
                    )}
                  </select>
                </LineItemField>
              )}
              <LineItemField label="Cant." required className={GIC.qty}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formatDecimalFieldValue(stagingItem.quantity, QUANTITY_INPUT_PATTERN)}
                  onChange={(e) => {
                    const raw = normalizeDecimalTyping(e.target.value);
                    if (raw === '' || QUANTITY_INPUT_PATTERN.test(raw)) {
                      handleStagingChange('quantity', raw);
                    }
                  }}
                  className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  placeholder="0"
                />
              </LineItemField>
              <LineItemField label="Unid." className={GIC.unit}>
                <UnitSelect
                  value={stagingItem.unit_of_measure || ''}
                  onChange={(v) => handleStagingChange('unit_of_measure', v)}
                  units={units}
                  product={findProductById(stagingItem.product_id, stagingItem.product)}
                  compact
                  emptyLabel="—"
                  showCodeOnly
                />
              </LineItemField>
              {showProjectField && (
                <LineItemField grow label="Proy." className={GIC.project}>
                  <select
                    value={stagingItem.project_id || ''}
                    onChange={(e) => handleStagingChange('project_id', e.target.value)}
                    className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  >
                    <option value="">Seleccionar</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.code} - {project.name}
                      </option>
                    ))}
                  </select>
                </LineItemField>
              )}
              {(!isExit || relaxStockRestrictions) && isEntryGuide && (
                <>
                  <LineItemField
                    label="C.Unit."
                    required
                    className={GIC.price}
                    hint={
                      <PriceIgvHint
                        netPrice={stagingItem.unit_price}
                        igvRate={ENTRY_GUIDE_IGV_RATE}
                        hasIgv={stagingItem.has_igv ?? true}
                        pricesIncludeIgv={pricesIncludeIgv}
                      />
                    }
                  >
                    <div className="relative w-full">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">S/</span>
                      <UnitPriceTextInput
                        netPrice={stagingItem.unit_price}
                        onNetPriceChange={(net) => handleStagingChange('unit_price', net)}
                        pricesIncludeIgv={pricesIncludeIgv}
                        hasIgv={stagingItem.has_igv ?? true}
                        igvRate={ENTRY_GUIDE_IGV_RATE}
                        className="w-full h-[34px] pl-6 pr-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                        placeholder="0.0000"
                      />
                    </div>
                  </LineItemField>
                  <LineItemField label="IGV" className={GIC.igv}>
                    <select
                      value={(stagingItem.has_igv ?? true) ? 'true' : 'false'}
                      onChange={(e) => handleStagingChange('has_igv', e.target.value)}
                      className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                    >
                      <option value="true">Sí</option>
                      <option value="false">No</option>
                    </select>
                  </LineItemField>
                </>
              )}
              {((isExit && !relaxStockRestrictions) || (useReferenceLastPurchaseCost && (isExit || isTransfer || isReception))) && !isEntryGuide && (
                <LineItemField
                  label={useReferenceLastPurchaseCost ? 'C.Unit. ref.' : 'C.Unit.'}
                  className={GIC.priceNarrow}
                  hint={renderOperationalCostHint(stagingItem, 'staging')}
                >
                  {useReferenceLastPurchaseCost && isReferenceCostEditable(stagingItem) ? (
                    <input
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      value={getStagingDisplayUnitPrice(stagingItem)}
                      onChange={(e) => {
                        const raw = normalizeDecimalTyping(e.target.value);
                        if (raw === '' || DECIMAL_INPUT_PATTERN.test(raw)) {
                          handleStagingChange('reference_unit_price', raw);
                        }
                      }}
                      onBlur={(e) => {
                        setStagingItem((prev) => commitManualReferenceCost(prev, e.target.value));
                      }}
                      className="w-full h-[34px] px-1.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                      placeholder="0.0000"
                    />
                  ) : (
                    <input
                      type="text"
                      inputMode="decimal"
                      readOnly
                      value={getStagingDisplayUnitPrice(stagingItem)}
                      className="w-full h-[34px] px-1.5 py-1.5 text-sm border border-gray-300 rounded-md bg-gray-100 cursor-not-allowed focus:outline-none"
                    />
                  )}
                </LineItemField>
              )}
              {(!isExit || relaxStockRestrictions) && !isEntryGuide && !useReferenceLastPurchaseCost && (
                <LineItemField label="C.Unit." required className={GIC.priceNarrow}>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={getStagingDisplayUnitPrice(stagingItem)}
                    onChange={(e) => {
                      const raw = normalizeDecimalTyping(e.target.value);
                      if (raw === '' || DECIMAL_INPUT_PATTERN.test(raw)) {
                        handleStagingChange('unit_price', raw);
                      }
                    }}
                    className="w-full h-[34px] px-1.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  />
                </LineItemField>
              )}
              {(isExit || isTransfer) && (
                <LineItemField label="Cond." required className={GIC.condition}>
                  <select
                    value={stagingItem.condition || 'good'}
                    onChange={(e) => handleStagingChange('condition', e.target.value)}
                    className="w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  >
                    <option value="good">Bueno</option>
                    <option value="damaged">Dañado</option>
                    <option value="defective">Defectuoso</option>
                    <option value="reusable">Reutilizable</option>
                    <option value="non_reusable">No reutilizable</option>
                  </select>
                </LineItemField>
              )}
            </QuickProductLineBar>
          )}

          {formData.items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No hay items agregados</p>
              <p className="text-sm mt-2">Selecciona una orden/documento o agrega items manualmente</p>
            </div>
          ) : (
            <div className="space-y-3">
              {formData.items.map((item, index) => {
                const isFromOrder = item.purchase_order_item_id || item.document_item_id;
                const isFromTransfer = isReception && item.transfer_guide_item_id;
                
                // Verificar si la guía tiene referencia a un documento (document_id)
                const hasDocumentReference = Boolean(
                  formData.document_id
                  || entryGuide?.document_id
                  || referenceSnapshot?.document_id,
                );
                
                // Obtener la cantidad máxima permitida si viene de una orden/documento
                let maxQuantity = null;
                const rowProduct = findProductById(item.product_id, item.product);

                if (isFromOrder) {
                  if (item.purchase_order_item_id) {
                    const order = findPurchaseOrderForItem(item);
                    const originalItem = order?.items?.find(
                      (oi) => Number(oi.id) === Number(item.purchase_order_item_id),
                    );
                    if (originalItem) {
                      const pendingRaw = originalItem.pending_quantity ?? originalItem.quantity;
                      if (item.uses_converted_display && rowProduct) {
                        const display = applyConvertedLineDisplay({
                          product: rowProduct,
                          unitCode: originalItem.unit_of_measure || item._source_unit_of_measure || '',
                          quantity: pendingRaw,
                          productConversionsEnabled,
                          useConvertedQuantityForSubtotal: true,
                          units,
                        });
                        maxQuantity = display.usesConvertedDisplay ? display.quantity : pendingRaw;
                      } else {
                        maxQuantity = pendingRaw;
                      }
                    }
                  } else if (item.document_item_id) {
                    const document = allPendingDocuments.find((doc) =>
                      doc.items?.some((di) => di.id === item.document_item_id),
                    );
                    if (document) {
                      const originalItem = document.items?.find(di => di.id === item.document_item_id);
                      if (originalItem) {
                        maxQuantity = originalItem.pending_quantity || originalItem.quantity;
                      }
                    }
                  }
                }
                
                // Para recepciones desde traspaso: usar cantidad original guardada
                if (isFromTransfer && item.original_quantity) {
                  maxQuantity = parseFloat(item.original_quantity);
                }
                
                const currentQuantity = parseFloat(item.quantity) || 0;
                // Solo bloquear si no hay cantidad pendiente; permitir editar aunque el valor sea 1
                const isQuantityBlocked =
                  (isFromOrder || isFromTransfer) &&
                  maxQuantity !== null &&
                  parseFloat(maxQuantity) <= 0;
                const isQuantityBlockedByReference = false;
                
                // Para recepciones: determinar qué campos son editables
                // Solo unidad y condición son editables por defecto
                // Si cantidad > 1, todos los campos son editables (excepto producto y evento)
                const canEditAllFields = isFromTransfer && currentQuantity > 1;
                
                // Si tiene referencia a documento, solo la cantidad es editable (puede ser 1 pero no editable, editable si > 1)
                const canEditQuantityOnly = hasDocumentReference && currentQuantity > 1;
                
                const showReferenceCostsOnly = useReferenceLastPurchaseCost && (isExit || isTransfer || isReception);
                const displayLineTotal = getItemFormDisplayTotal(item, showReferenceCostsOnly);
                const referenceCostEditable = showReferenceCostsOnly && isReferenceCostEditable(item);
                
                // Valor a mostrar en Costo Unit.: referencial en modo documento; operativo si no
                const displayUnitPrice = (() => {
                  if (showReferenceCostsOnly) {
                    const ref = item.reference_unit_price;
                    if (ref !== undefined && ref !== null && ref !== '') {
                      if (typeof ref === 'string' && isDecimalInputString(ref)) {
                        return normalizeDecimalTyping(ref);
                      }
                      const refNum = Number(ref);
                      if (!Number.isNaN(refNum)) return String(refNum);
                    }
                    return '0';
                  }
                  const up = item.unit_price;
                  if (typeof up === 'string' && isDecimalInputString(up)) {
                    return normalizeDecimalTyping(up);
                  }
                  const num = Number(up);
                  if (up !== undefined && up !== null && up !== '' && !Number.isNaN(num)) return String(num);
                  const qty = parseDecimalNumber(item.quantity);
                  const tot = Number(item.total);
                  if (qty > 0 && tot !== undefined && tot !== null && tot !== '' && !Number.isNaN(tot)) {
                    return String(Math.round((tot / qty) * 10000) / 10000);
                  }
                  return '';
                })();
                
                // Stock a mostrar: item.stock_info o desde caché (stockCostByProduct) para salida/traspaso
                const displayStockInfo = (() => {
                  if (item.stock_info) return item.stock_info;
                  if ((isExit || isTransfer) && item.product_id && formData.warehouse_id) {
                    const cached = stockCostByProduct[item.product_id] || stockCostByProduct[String(item.product_id)];
                    if (cached) return buildStockInfoFromCache(cached);
                  }
                  return null;
                })();

                return (
                  <div key={index} className={`border rounded-lg p-3 ${isFromOrder ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
                    {/* Referencia (solo para evento Devolución) */}
                    {item.kardex_event === 'devolucion' && (
                      <div className="mb-2 px-2 py-1.5 bg-teal-50 border border-teal-200 rounded text-sm">
                        <span className="font-medium text-teal-800">Referencia:</span>{' '}
                        <span className="text-teal-700">{item.consumption_exit_guide_code || (item.consumption_exit_guide && item.consumption_exit_guide.code) || (item.consumptionExitGuide && item.consumptionExitGuide.code) || '—'}</span>
                        {!isExit && (
                          <button
                            type="button"
                            onClick={async () => {
                              if (!formData.supplier_id || !item.product_id) {
                                setToast({
                                  isOpen: true,
                                  message: 'Debe seleccionar un proveedor y un producto para elegir la referencia',
                                  type: 'error'
                                });
                                return;
                              }
                              openedReturnModalFromSelectRef.current = true;
                              setCurrentItemIndex(index);
                              setIsReturnModalOpen(true);
                              await loadConsumptionExitGuides(formData.supplier_id, item.product_id, formData.warehouse_id);
                            }}
                            className="ml-2 text-teal-600 hover:text-teal-800 underline text-xs font-medium"
                          >
                            {item.consumption_exit_guide_code || (item.consumption_exit_guide && item.consumption_exit_guide.code) || (item.consumptionExitGuide && item.consumptionExitGuide.code) ? 'Cambiar referencia' : 'Seleccionar referencia'}
                          </button>
                        )}
                      </div>
                    )}
                    <div className={GUIDE_ITEM_ROW}>
                      <LineItemField
                        grow
                        label="Producto"
                        required
                        className={GIC.product}
                        hint={
                          errors[`items.${index}.product_id`] ? (
                            <span className="text-red-600 truncate" title={errors[`items.${index}.product_id`]}>
                              {errors[`items.${index}.product_id`]}
                            </span>
                          ) : null
                        }
                      >
                        <ProductSelector
                          ref={(el) => {
                            if (el) {
                              productSelectorRefs.current[index] = el;
                            } else {
                              delete productSelectorRefs.current[index];
                            }
                          }}
                          value={item.product_id}
                          onChange={(value) => handleItemChange(index, 'product_id', value)}
                          {...productSelectorAsyncProps}
                          error={errors[`items.${index}.product_id`]}
                          compact={true}
                          disabled={isFromTransfer || hasDocumentReference}
                          productStockMap={(isExit || isTransfer) ? stockCostByProduct : undefined}
                        />
                      </LineItemField>

                      {!(isTransfer || isReception) && (
                      <LineItemField
                        label="Kardex"
                        required
                        className={GIC.kardex}
                        hint={
                          errors[`items.${index}.kardex_event`] ? (
                            <span className="text-red-600 truncate" title={errors[`items.${index}.kardex_event`]}>
                              {errors[`items.${index}.kardex_event`]}
                            </span>
                          ) : null
                        }
                      >
                        <select
                          value={item.kardex_event || (isInitial ? 'saldo_inicial' : (isExit ? 'venta' : 'compra'))}
                          onChange={async (e) => {
                            const newValue = e.target.value;
                            console.log('Cambio de evento kardex:', newValue, 'isExit:', isExit);
                            if (newValue === 'devolucion' && !isExit) {
                              // Validar que haya proveedor y producto antes de abrir el modal
                              if (!formData.supplier_id || !item.product_id) {
                                setToast({
                                  isOpen: true,
                                  message: 'Debe seleccionar un proveedor y un producto antes de seleccionar Devolución',
                                  type: 'error'
                                });
                                // Revertir el select
                                e.target.value = item.kardex_event || 'compra';
                                return;
                              }
                              // Mantener el evento en devolución y abrir modal
                              openedReturnModalFromSelectRef.current = false;
                              handleItemChange(index, 'kardex_event', 'devolucion');
                              setCurrentItemIndex(index);
                              setIsReturnModalOpen(true);
                              await loadConsumptionExitGuides(formData.supplier_id, item.product_id, formData.warehouse_id);
                            } else {
                              handleItemChange(index, 'kardex_event', newValue);
                            }
                          }}
                          className={`w-full h-[34px] px-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 transition-all ${
                            errors[`items.${index}.kardex_event`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                          } ${(isInitial || isFromOrder || isFromTransfer || hasDocumentReference) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          disabled={isInitial || isFromOrder || isFromTransfer || hasDocumentReference}
                        >
                          {isInitial ? (
                            <option value="saldo_inicial">Saldo Inicial</option>
                          ) : isExit ? (
                            <>
                              <option value="venta">Venta</option>
                              <option value="salida_produccion">Salida a producción</option>
                              <option value="descuento_concedido">Descuento Concedido</option>
                              <option value="consumo">Consumo</option>
                              <option value="transferencia_gratuita">Transferencia Gratuita</option>
                              <option value="descuento_con_mercaderia">Descuento con Mercadería</option>
                            </>
                          ) : (
                            <>
                              <option value="compra">Compra</option>
                              <option value="entrada_produccion">Entrada de producción</option>
                              <option value="entrada_producto_terminado">Entrada producto terminado</option>
                              <option value="devolucion">Devolución</option>
                              <option value="transferencia_gratuita">Transferencia Gratuita</option>
                              <option value="descuento_obtenido">Descuento Obtenido</option>
                              <option value="descuento_con_mercaderia">Descuento con Mercadería</option>
                            </>
                          )}
                        </select>
                      </LineItemField>
                      )}

                      <LineItemField
                        label="Cant."
                        required
                        className={GIC.qty}
                        hint={
                          errors[`items.${index}.quantity`] ? (
                            <span className="text-red-600 truncate" title={errors[`items.${index}.quantity`]}>
                              {errors[`items.${index}.quantity`]}
                            </span>
                          ) : !item.uses_converted_display ? (
                            <QuantityConversionHint
                              product={rowProduct}
                              unitCode={item.unit_of_measure}
                              quantity={item.quantity}
                            />
                          ) : null
                        }
                      >
                        <input
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          value={formatDecimalFieldValue(item.quantity, QUANTITY_INPUT_PATTERN)}
                          onChange={(e) => {
                            const raw = normalizeDecimalTyping(e.target.value);
                            if (raw === '') {
                              handleItemChange(index, 'quantity', '');
                              return;
                            }
                            if (!QUANTITY_INPUT_PATTERN.test(raw)) return;
                            handleItemChange(index, 'quantity', raw);
                          }}
                          onBlur={(e) => {
                            const parsed = parseDecimalFieldInput(e.target.value, QUANTITY_INPUT_PATTERN);
                            if (parsed.empty) {
                              handleItemChange(index, 'quantity', '');
                              return;
                            }
                            if (parsed.invalid) return;
                            let finalValue = parsed.value;
                            if (finalValue > 0 && finalValue < 0.0001) finalValue = 0.0001;
                            if ((isFromOrder || isFromTransfer) && maxQuantity !== null) {
                              finalValue = Math.min(finalValue, parseFloat(maxQuantity));
                            }
                            handleItemChange(index, 'quantity', Math.round(finalValue * 1000) / 1000);
                          }}
                          disabled={isQuantityBlocked || isQuantityBlockedByReference}
                          className={`w-full h-[34px] px-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 transition-all ${
                            errors[`items.${index}.quantity`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                          } ${(isQuantityBlocked || isQuantityBlockedByReference) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        />
                      </LineItemField>

                      <LineItemField label="Unid." className={GIC.unit}>
                        <UnitSelect
                          value={item.unit_of_measure || ''}
                          onChange={(v) => handleItemChange(index, 'unit_of_measure', v)}
                          units={units}
                          product={findProductById(item.product_id, item.product)}
                          compact
                          disabled={isFromOrder || (isFromTransfer && !canEditAllFields) || hasDocumentReference}
                          emptyLabel="—"
                          showCodeOnly
                        />
                      </LineItemField>

                      {showProjectField && (
                        <LineItemField grow label="Proy." className={GIC.project}>
                          <select
                            value={item.project_id || ''}
                            onChange={(e) => handleItemChange(index, 'project_id', e.target.value)}
                            className={`w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all ${(isFromTransfer && !canEditAllFields) || hasDocumentReference ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            disabled={isFromOrder || (isFromTransfer && !canEditAllFields) || hasDocumentReference}
                          >
                            <option value="">Seleccionar</option>
                            {projects.map((project) => (
                              <option key={project.id} value={project.id}>
                                {project.code} - {project.name}
                              </option>
                            ))}
                          </select>
                        </LineItemField>
                      )}

                      {((isExit && !relaxStockRestrictions) || (useReferenceLastPurchaseCost && (isExit || isTransfer || isReception))) && (
                        <LineItemField
                          label={useReferenceLastPurchaseCost ? 'C.Unit. ref.' : 'C.Unit.'}
                          required={!useReferenceLastPurchaseCost}
                          className={GIC.priceNarrow}
                          hint={(
                            <span className="flex items-center justify-between gap-1 min-w-0">
                              {renderOperationalCostHint(item, index, {
                                disabled: (isFromTransfer && !canEditAllFields) || hasDocumentReference,
                              })}
                              {errors[`items.${index}.unit_price`] ? (
                                <span className="text-red-600 truncate" title={errors[`items.${index}.unit_price`]}>
                                  {errors[`items.${index}.unit_price`]}
                                </span>
                              ) : null}
                            </span>
                          )}
                        >
                          {referenceCostEditable ? (
                            <input
                              type="text"
                              inputMode="decimal"
                              autoComplete="off"
                              value={displayUnitPrice}
                              onChange={(e) => {
                                const raw = normalizeDecimalTyping(e.target.value);
                                if (raw === '') {
                                  handleItemChange(index, 'reference_unit_price', '');
                                  return;
                                }
                                if (!DECIMAL_INPUT_PATTERN.test(raw)) return;
                                handleItemChange(index, 'reference_unit_price', raw);
                              }}
                              onBlur={(e) => {
                                setFormData((prev) => {
                                  const newItems = [...prev.items];
                                  newItems[index] = commitManualReferenceCost(newItems[index], e.target.value);
                                  return { ...prev, items: newItems };
                                });
                              }}
                              className={`w-full h-[34px] px-1.5 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 transition-all ${
                                errors[`items.${index}.unit_price`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                              }`}
                              placeholder="0.0000"
                            />
                          ) : (
                            <input
                              type="text"
                              inputMode="decimal"
                              readOnly
                              value={
                                displayUnitPrice !== ''
                                  ? String(displayUnitPrice)
                                  : (item.unit_price === 0 || item.unit_price === '0' ? '0' : '')
                              }
                              className={`w-full h-[34px] px-1.5 py-1.5 text-sm border rounded-md bg-gray-100 cursor-not-allowed focus:outline-none ${
                                errors[`items.${index}.unit_price`] ? 'border-red-300' : 'border-gray-300'
                              }`}
                            />
                          )}
                        </LineItemField>
                      )}
                      {(!isExit || relaxStockRestrictions) && isEntryGuide && (
                        <>
                          <LineItemField
                            label="C.Unit."
                            required
                            className={GIC.price}
                            hint={
                              errors[`items.${index}.unit_price`] ? (
                                <span className="text-red-600 truncate" title={errors[`items.${index}.unit_price`]}>
                                  {errors[`items.${index}.unit_price`]}
                                </span>
                              ) : (
                                <PriceIgvHint
                                  netPrice={item.unit_price}
                                  igvRate={ENTRY_GUIDE_IGV_RATE}
                                  hasIgv={item.has_igv ?? true}
                                  pricesIncludeIgv={pricesIncludeIgv}
                                />
                              )
                            }
                          >
                            <div className="relative w-full">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">S/</span>
                              <UnitPriceTextInput
                                netPrice={item.unit_price}
                                onNetPriceChange={(net) => handleItemChange(index, 'unit_price', net)}
                                pricesIncludeIgv={pricesIncludeIgv}
                                hasIgv={item.has_igv ?? true}
                                igvRate={ENTRY_GUIDE_IGV_RATE}
                                disabled={(isFromTransfer && !canEditAllFields) || hasDocumentReference}
                                className={`w-full h-[34px] pl-6 pr-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 transition-all ${
                                  errors[`items.${index}.unit_price`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                                } ${((isFromTransfer && !canEditAllFields) || hasDocumentReference) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                placeholder="0.0000"
                              />
                            </div>
                          </LineItemField>
                          <LineItemField label="IGV" className={GIC.igv}>
                            <select
                              value={(item.has_igv ?? true) ? 'true' : 'false'}
                              onChange={(e) => handleItemChange(index, 'has_igv', e.target.value)}
                              disabled={hasDocumentReference}
                              className={`w-full h-[34px] px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 transition-all ${
                                hasDocumentReference ? 'bg-gray-100 cursor-not-allowed' : ''
                              }`}
                            >
                              <option value="true">Sí</option>
                              <option value="false">No</option>
                            </select>
                          </LineItemField>
                        </>
                      )}
                      {(!isExit || relaxStockRestrictions) && !isEntryGuide && !useReferenceLastPurchaseCost && (
                        <LineItemField
                          label="C.Unit."
                          required
                          className={GIC.priceNarrow}
                          hint={
                            errors[`items.${index}.unit_price`] ? (
                              <span className="text-red-600 truncate" title={errors[`items.${index}.unit_price`]}>
                                {errors[`items.${index}.unit_price`]}
                              </span>
                            ) : null
                          }
                        >
                          <input
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            value={displayUnitPrice}
                            onChange={(e) => {
                              const raw = normalizeDecimalTyping(e.target.value);
                              if (raw === '') {
                                handleItemChange(index, 'unit_price', '');
                                return;
                              }
                              if (!DECIMAL_INPUT_PATTERN.test(raw)) return;
                              handleItemChange(index, 'unit_price', raw);
                            }}
                            onBlur={(e) => {
                              const parsed = parseDecimalFieldInput(e.target.value);
                              if (parsed.empty) {
                                handleItemChange(index, 'unit_price', '');
                                return;
                              }
                              if (parsed.invalid) return;
                              handleItemChange(
                                index,
                                'unit_price',
                                Math.round(parsed.value * 10000) / 10000
                              );
                            }}
                            disabled={(isFromTransfer && !canEditAllFields) || hasDocumentReference}
                            className={`w-full h-[34px] px-1.5 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 transition-all ${
                              errors[`items.${index}.unit_price`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                            } ${((isFromTransfer && !canEditAllFields) || hasDocumentReference) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          />
                        </LineItemField>
                      )}

                      <LineItemField
                        label="Total"
                        className={GIC.total}
                        hint={
                          isEntryGuide ? (
                            <PriceIgvHint
                              netPrice={item.total}
                              igvRate={ENTRY_GUIDE_IGV_RATE}
                              hasIgv={item.has_igv ?? true}
                              alwaysConIgv
                            />
                          ) : null
                        }
                      >
                        <input
                          type="text"
                          value={displayLineTotal.toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                          className="w-full h-[34px] px-1.5 py-1.5 text-sm border border-gray-300 rounded-md bg-gray-100 text-gray-700"
                          readOnly
                        />
                      </LineItemField>

                      <LineItemField
                        label="Cond."
                        required
                        className={GIC.condition}
                        hint={
                          errors[`items.${index}.condition`] ? (
                            <span className="text-red-600 truncate" title={errors[`items.${index}.condition`]}>
                              {errors[`items.${index}.condition`]}
                            </span>
                          ) : null
                        }
                      >
                        <select
                          value={item.condition || 'good'}
                          onChange={(e) => handleItemChange(index, 'condition', e.target.value)}
                          className={`w-full h-[34px] px-2 py-1.5 text-sm border rounded-md focus:outline-none focus:ring-1 transition-all ${
                            errors[`items.${index}.condition`] ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                          }`}
                        >
                          <option value="good">Bueno</option>
                          <option value="damaged">Dañado</option>
                          <option value="defective">Defectuoso</option>
                          <option value="reusable">Reutilizable</option>
                          <option value="non_reusable">No reutilizable</option>
                        </select>
                      </LineItemField>

                      <LineItemField hideLabel className={GIC.actions}>
                        <div className="flex flex-col items-center gap-1 w-full">
                          {isExit && (() => {
                            const product = findProductById(item.product_id, item.product);
                            return product?.machinery_register_enabled ? (
                              <button
                                type="button"
                                onClick={() => setEventModalItemIndex(index)}
                                className="h-[34px] w-full flex items-center justify-center bg-teal-100 text-teal-700 rounded-md hover:bg-teal-200 transition-colors"
                                title="Registrar avance (evento de seguimiento)"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                </svg>
                              </button>
                            ) : null;
                          })()}
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            disabled={hasDocumentReference || formData.items.length === 1}
                            className={`h-[34px] w-full flex items-center justify-center bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm ${(hasDocumentReference || formData.items.length === 1) ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title={
                              hasDocumentReference
                                ? 'No se puede eliminar items de una guía con referencia'
                                : formData.items.length === 1
                                ? 'Debe haber al menos un item en la guía'
                                : 'Eliminar'
                            }
                          >
                            ✕
                          </button>
                        </div>
                      </LineItemField>
                    </div>
                    {/* Nombre completo del producto y resumen de evento (guía de salida) */}
                    {item.product_id && (() => {
                      const product = findProductById(item.product_id, item.product);
                      const name = product?.name;
                      const pendingSummaries = isExit ? pendingMachineryEvents.filter(e => e.itemIndex === index).map(e => e.displaySummary).filter(Boolean) : [];
                      const loadedForItem = isExit ? loadedMachineryEventsByItem.filter(e => e.itemIndex === index) : [];
                      const summariesToShow = pendingSummaries.length > 0 ? pendingSummaries : loadedForItem.map((e) => e.displaySummary);
                      const hasEvents = isExit && summariesToShow.length > 0;
                      if (!name && !hasEvents) return null;
                      return (
                        <div className="mt-1.5 mb-0">
                          {name && (
                            <p className="text-xs text-gray-600 truncate" title={name}>{name}</p>
                          )}
                          {hasEvents && (
                            <div className="text-xs text-teal-600/90 mt-0.5 space-y-0.5">
                              {summariesToShow.map((summary, i) => (
                                <p key={i} className="truncate" title={summary}>{summary}</p>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {/* Stock y costo (salida / traspaso) — una línea sutil */}
                    {(isExit || isTransfer) && displayStockInfo && item.product_id && (() => {
                      const totalStock = Number(displayStockInfo.total_stock ?? 0);
                      const unit = item.unit_of_measure || '';
                      const sbc = displayStockInfo.stock_by_condition || {};
                      const avgCost = Number(displayStockInfo.average_cost) || 0;
                      const refCost = item.reference_unit_price != null && item.reference_unit_price !== ''
                        ? Number(item.reference_unit_price)
                        : 0;
                      const cachedForCost = item.product_id
                        ? (stockCostByProduct[item.product_id] || stockCostByProduct[String(item.product_id)])
                        : null;
                      const summaryUnitCost = showReferenceCostsOnly
                        ? (refCost > 0 ? refCost : roundExitTransferUnitCost(cachedForCost?.last_purchase_cost ?? 0))
                        : avgCost;
                      const conditionParts = [
                        { key: 'good', label: 'Bueno', tone: Number(sbc.good) < 0 ? 'text-red-500' : 'text-gray-500' },
                        { key: 'damaged', label: 'Dañado', tone: Number(sbc.damaged) < 0 ? 'text-red-500' : 'text-gray-500' },
                        { key: 'defective', label: 'Defectuoso', tone: 'text-red-500' },
                        { key: 'reusable', label: 'Reutilizable', tone: Number(sbc.reusable) < 0 ? 'text-red-500' : 'text-gray-500' },
                        { key: 'non_reusable', label: 'No reutilizable', tone: Number(sbc.non_reusable) < 0 ? 'text-red-500' : 'text-gray-500' },
                      ].filter(({ key }) => ((sbc[key]) ?? 0) !== 0);

                      const summaryTitle = [
                        `Stock ${totalStock.toFixed(3)} ${unit}`.trim(),
                        ...conditionParts.map(({ key, label }) => `${label}: ${Number(sbc[key]).toFixed(3)}`),
                        summaryUnitCost > 0
                          ? (showReferenceCostsOnly
                            ? `C.ref.: S/ ${summaryUnitCost.toFixed(4)}`
                            : `Costo: S/ ${summaryUnitCost.toFixed(4)}`)
                          : null,
                      ].filter(Boolean).join(' · ');

                      return (
                        <p className="mt-1 text-[11px] leading-snug text-gray-400 truncate" title={summaryTitle}>
                          <span className={totalStock < 0 ? 'text-red-500' : undefined}>
                            Stock {totalStock.toFixed(3)}{unit ? ` ${unit}` : ''}
                          </span>
                          {conditionParts.map(({ key, label, tone }) => (
                            <span key={key}>
                              <span className="mx-1 text-gray-300" aria-hidden>·</span>
                              <span className={tone}>{label}: {Number(sbc[key]).toFixed(3)}</span>
                            </span>
                          ))}
                          {summaryUnitCost > 0 && (
                            <>
                              <span className="mx-1 text-gray-300" aria-hidden>·</span>
                              <span>{showReferenceCostsOnly ? `C.ref. S/ ${summaryUnitCost.toFixed(4)}` : `Costo S/ ${summaryUnitCost.toFixed(4)}`}</span>
                            </>
                          )}
                        </p>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          )}

          {/* Total de Items */}
          {formData.items.length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="flex justify-end">
                <div className="w-full sm:w-auto bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-200 rounded-lg p-3 sm:p-4 sm:min-w-[300px]">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-700">Total:</span>
                    <span className="text-2xl font-bold text-teal-700">
                      S/ {formData.items.reduce(
                        (sum, item) => sum + getItemFormDisplayTotal(
                          item,
                          useReferenceLastPurchaseCost && (isExit || isTransfer || isReception),
                        ),
                        0,
                      ).toLocaleString('es-PE', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                    </span>
                  </div>
                  {isEntryGuide && (() => {
                    const referentialConIgv = getGuideGrandTotalConIgvReferential(formData.items, ENTRY_GUIDE_IGV_RATE);
                    if (!referentialConIgv) return null;
                    return (
                      <p className="mt-1 text-right text-[10px] sm:text-xs text-teal-700/90 leading-tight">
                        Referencial: S/ {referentialConIgv} con IGV
                      </p>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Botones de acción */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Guardando...' : (entryGuide ? 'Actualizar' : 'Crear')} Guía
          </button>
        </div>
      </div>

      {/* Modal de Selección de Órdenes/Documentos */}
      {isOrderSelectorModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">Seleccionar referencias</h3>
                <button
                  onClick={() => setIsOrderSelectorModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6">
              {referencesLoading ? (
                <div className="py-16 flex flex-col items-center justify-center text-gray-500">
                  <FormLoadingSpinner />
                  <p className="mt-4 text-sm">Cargando referencias pendientes…</p>
                </div>
              ) : (
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-gray-800 mb-2">Requerimientos internos</h4>
                  <p className="text-xs text-gray-500 mb-3">Sin proveedor. Carga cantidades pendientes del REQ.</p>
                  <RequirementReferencePicker
                    mode="single"
                    requirements={pendingRequirements}
                    onSelectSingle={handleSelectRequirement}
                  />
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-lg font-medium text-gray-800 mb-4">Órdenes de Compra</h4>
                  {(() => {
                    // Filtrar órdenes que tienen items con cantidad pendiente > 0
                    const ordersWithPendingItems = pendingPurchaseOrders.filter(order => {
                      return order.items?.some(item => parseFloat(item.pending_quantity || 0) > 0.001);
                    });
                    
                    if (ordersWithPendingItems.length === 0) {
                      return <p className="text-gray-500 text-sm">No hay órdenes de compra pendientes</p>;
                    }
                    
                    return (
                      <div className="space-y-2">
                        {ordersWithPendingItems.map(order => {
                          const { pending, total } = countPendingPurchaseOrderItems(order);

                          return (
                            <div
                              key={order.id}
                              onClick={() => handleSelectPurchaseOrder(order.id)}
                              className="p-4 border border-gray-200 rounded-lg hover:border-teal-500 hover:bg-teal-50 cursor-pointer transition-colors"
                            >
                              <div className="flex justify-between items-start gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-gray-900">{order.code}</div>
                                  <div className="text-sm text-gray-600 mt-1">
                                    Entidad: {order.supplier?.name || 'N/A'}
                                  </div>
                                  <div className="text-sm text-gray-600 mt-0.5">
                                    Ítems pendientes: {pending} de {total}
                                  </div>
                                  <div className="mt-2 max-w-xs">
                                    <PurchaseOrderProgressBar order={order} barClassName="h-2" />
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="shrink-0 px-3 py-1 bg-teal-600 text-white rounded-md hover:bg-teal-700 text-sm"
                                >
                                  Seleccionar
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>

                {/* Documentos */}
                <div>
                  <h4 className="text-lg font-medium text-gray-800 mb-4">Documentos</h4>
                  {pendingDocuments.length === 0 ? (
                    <p className="text-gray-500 text-sm">No hay documentos pendientes</p>
                  ) : (
                    <div className="space-y-2">
                      {pendingDocuments.map(doc => {
                        // Calcular items pendientes (usando tolerancia de 0.001 para evitar problemas de precisión)
                        const pendingItems = doc.items?.filter(item => parseFloat(item.pending_quantity || 0) > 0.001) || [];
                        const totalItems = doc.items?.length || 0;
                        const docSerieNumero = (doc.series || doc.number)
                          ? `${doc.series || ''}${doc.series && doc.number ? '-' : ''}${doc.number || ''}`
                          : null;
                        
                        return (
                          <div
                            key={doc.id}
                            onClick={() => handleSelectDocument(doc.id)}
                            className="p-4 border border-gray-200 rounded-lg hover:border-teal-500 hover:bg-teal-50 cursor-pointer transition-colors"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="font-medium text-gray-900">{doc.code}</div>
                                <div className="text-sm text-gray-600 mt-1">
                                  Tipo: {doc.document_type} | Entidad: {doc.supplier?.name || 'N/A'}
                                </div>
                                {docSerieNumero && (
                                  <div className="text-sm text-gray-600">
                                    Serie/Número: {docSerieNumero}
                                  </div>
                                )}
                                <div className="text-sm text-gray-600">
                                  Items pendientes: {pendingItems.length} de {totalItems}
                                </div>
                              </div>
                              <button className="px-3 py-1 bg-teal-600 text-white rounded-md hover:bg-teal-700 text-sm">
                                Seleccionar
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Selección de Guías de Traspaso - Solo para recepciones */}
      {isTransferGuideSelectorModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">Seleccionar Guía de Traspaso</h3>
                <button
                  onClick={() => setIsTransferGuideSelectorModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6">
              {availableTransferGuides.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">No hay guías de traspaso pendientes disponibles</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {availableTransferGuides.map(guide => (
                    <div
                      key={guide.id}
                      onClick={() => {
                        // Calcular items pendientes de la guía de traspaso
                        const pendingItems = [];
                        
                        if (guide.items && guide.items.length > 0) {
                          guide.items.forEach(transferItem => {
                            // Calcular cantidad recibida de este item en todas las recepciones
                            let receivedQuantity = 0;
                            
                            if (guide.receptions && guide.receptions.length > 0) {
                              guide.receptions.forEach(reception => {
                                if (reception.items && reception.items.length > 0) {
                                  reception.items.forEach(receptionItem => {
                                    // Si es el mismo producto, sumar la cantidad recibida
                                    if (receptionItem.product_id === transferItem.product_id) {
                                      receivedQuantity += parseFloat(receptionItem.quantity || 0);
                                    }
                                  });
                                }
                              });
                            }
                            
                            const transferQuantity = parseFloat(transferItem.quantity || 0);
                            const pendingQuantity = Math.max(0, transferQuantity - receivedQuantity);
                            
                            // Solo agregar items con cantidad pendiente significativa (> 0.001)
                            if (pendingQuantity > 0.001) {
                              // Calcular el monto pendiente proporcional
                              const transferUnitPrice = Math.round((parseFloat(transferItem.unit_price || 0)) * 10000) / 10000;
                              const pendingTotal = Math.round(pendingQuantity * transferUnitPrice * 10000) / 10000;
                              const refUnit = transferItem.reference_unit_price != null && transferItem.reference_unit_price !== ''
                                ? Math.round((parseFloat(transferItem.reference_unit_price) || 0) * 10000) / 10000
                                : null;
                              const pendingRefTotal = refUnit != null
                                ? Math.round(pendingQuantity * refUnit * 10000) / 10000
                                : null;
                              
                              pendingItems.push({
                                product_id: transferItem.product_id || '',
                                description: transferItem.description || '',
                                project_id: transferItem.project_id || '',
                                kardex_event: 'recepcion',
                                quantity: pendingQuantity,
                                original_quantity: transferQuantity,
                                unit_price: transferUnitPrice,
                                total: pendingTotal,
                                reference_unit_price: refUnit,
                                reference_total: pendingRefTotal,
                                unit_of_measure: transferItem.unit_of_measure || '',
                                condition: transferItem.condition || 'good',
                                transfer_guide_item_id: transferItem.id, // Guardar referencia al item original
                              });
                            }
                          });
                        }
                        
                        // Cargar solo los items pendientes
                        mergeProducts(
                          (guide.items || [])
                            .map((transferItem) => transferItem.product)
                            .filter(Boolean),
                        );
                        setSelectedTransferGuide(guide);
                        setFormData(prev => ({
                          ...prev,
                          transfer_guide_id: guide.id.toString(),
                          warehouse_id: guide.destination_warehouse_id?.toString() || prev.warehouse_id,
                          supplier_id: guide.supplier_id?.toString() || prev.supplier_id, // Cargar entidad del traspaso
                          items: pendingItems,
                        }));
                        setIsTransferGuideSelectorModalOpen(false);
                        setToast({
                          isOpen: true,
                          message: `Guía de traspaso seleccionada. Se cargaron ${pendingItems.length} items pendientes.`,
                          type: 'success'
                        });
                      }}
                      className="p-4 border border-gray-200 rounded-lg hover:border-teal-500 hover:bg-teal-50 cursor-pointer transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium text-gray-900">{guide.code}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            Desde: {guide.warehouse?.code || 'N/A'} - {guide.warehouse?.name || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-600">
                            Hacia: {guide.destination_warehouse?.code || 'N/A'} - {guide.destination_warehouse?.name || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-600">
                            Fecha: {formatDateLocaleLima(guide.issue_date)}
                          </div>
                          <div className="text-sm text-gray-600">
                            Items: {guide.items?.length || 0}
                          </div>
                        </div>
                        <button className="px-3 py-1 bg-teal-600 text-white rounded-md hover:bg-teal-700 text-sm">
                          Seleccionar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </form>
  );

  // Modal de devolución renderizado con portal (compartido para ambos modos)
  const returnModal = isReturnModalOpen && createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: 10000, position: 'fixed' }}>
      {console.log('Modal de devolución renderizado, isReturnModalOpen:', isReturnModalOpen, 'guides:', consumptionExitGuides.length)}
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800">
                Guías de Referencia para Devolución
              </h3>
              <button
                onClick={() => {
                  setIsReturnModalOpen(false);
                  setSelectedGuideIds([]);
                  setCurrentItemIndex(null);
                  // Solo revertir a compra si se abrió desde el dropdown (no desde "Seleccionar referencia") y había guías y el usuario cierra sin elegir
                  if (!openedReturnModalFromSelectRef.current && currentItemIndex !== null && (consumptionExitGuides.length > 0 || initialBalanceGuides.length > 0)) {
                    handleItemChange(currentItemIndex, 'kardex_event', 'compra');
                  }
                  openedReturnModalFromSelectRef.current = false;
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="px-4 py-3 flex-1 overflow-y-auto max-h-[60vh]">
              {loadingConsumptionGuides ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">Cargando guías de referencia...</p>
                </div>
              ) : consumptionExitGuides.length === 0 && initialBalanceGuides.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-700 font-medium">
                    No se encontraron guías de referencia para este producto.
                  </p>
                  <p className="text-gray-500 mt-2">
                    Puede continuar e ingresar cantidad y costo unitario manualmente (se asume que el sistema no tiene inventarios de referencia para este ítem).
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setIsReturnModalOpen(false);
                      setSelectedGuideIds([]);
                      setCurrentItemIndex(null);
                      openedReturnModalFromSelectRef.current = false;
                    }}
                    className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium"
                  >
                    Continuar e ingresar manualmente
                  </button>
                </div>
              ) : (
                <>
                  {/* Sección de Guías de Consumo */}
                  {consumptionExitGuides.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        Guías de Salida de Consumos ({consumptionExitGuides.length})
                      </h4>
                      <p className="text-xs text-gray-600 mb-3">
                        Seleccione una o más:
                      </p>
                      <div className="space-y-2">
                        {consumptionExitGuides.map((guide) => {
                      const isSelected = selectedGuideIds.includes(guide.id);
                      const item = guide.items && guide.items.length > 0 ? guide.items[0] : null;
                      return (
                        <div 
                          key={guide.id} 
                          className={`border rounded-lg p-2 transition-colors ${
                            item && item.available_balance <= 0.01
                              ? 'border-amber-200 bg-amber-50/50'
                              : isSelected 
                                ? 'border-teal-500 bg-teal-50 cursor-pointer' 
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                          } ${isSelected ? 'border-teal-500 bg-teal-50' : ''}`}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedGuideIds(selectedGuideIds.filter(id => id !== guide.id));
                            } else {
                              setSelectedGuideIds([...selectedGuideIds, guide.id]);
                            }
                          }}
                        >
                          <div className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {
                                if (isSelected) {
                                  setSelectedGuideIds(selectedGuideIds.filter(id => id !== guide.id));
                                } else {
                                  setSelectedGuideIds([...selectedGuideIds, guide.id]);
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                              className="mt-1 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-sm text-gray-800">{guide.code}</h4>
                                  <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                    {guide.status === 'issued' ? 'Emitida' : guide.status}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-500">
                                  {guide.issue_date ? formatDateLocaleLima(guide.issue_date) : 'N/A'}
                                </span>
                              </div>
                              {item && (
                                <div className="space-y-1 text-xs mt-1">
                                  <div className="grid grid-cols-3 gap-2">
                                    <div>
                                      <span className="text-gray-500">Cantidad Original:</span>
                                      <span className="ml-1 font-medium text-gray-800">
                                        {parseFloat(item.quantity || 0).toFixed(3)} {item.unit_of_measure || ''}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Costo Unit.:</span>
                                      <span className="ml-1 font-medium text-gray-800">
                                        S/ {parseFloat(item.unit_price || 0).toFixed(4)}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-gray-500">Total:</span>
                                      <span className="ml-1 font-medium text-gray-800">
                                        S/ {parseFloat(item.total || 0).toFixed(4)}
                                      </span>
                                    </div>
                                  </div>
                                  {item.returned_quantity !== undefined && (
                                    <div className="flex gap-4 text-xs">
                                      <div>
                                        <span className="text-gray-500">Devuelto:</span>
                                        <span className="ml-1 font-medium text-orange-600">
                                          {parseFloat(item.returned_quantity || 0).toFixed(3)} {item.unit_of_measure || ''}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Saldo Disponible:</span>
                                        <span className={`ml-1 font-bold ${
                                          parseFloat(item.available_balance || 0) > 0.01 
                                            ? 'text-green-600' 
                                            : 'text-amber-600'
                                        }`}>
                                          {parseFloat(item.available_balance || 0).toFixed(3)} {item.unit_of_measure || ''}
                                        </span>
                                        {parseFloat(item.available_balance || 0) <= 0.01 && (
                                          <span className="ml-1 text-xs text-amber-600">(solo referencia)</span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                              {guide.warehouse && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Almacén: {guide.warehouse.code}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                      </div>
                    </div>
                  )}

                  {/* Sección de Guías de Saldo Inicial */}
                  {initialBalanceGuides.length > 0 && (
                    <div className={consumptionExitGuides.length > 0 ? 'mt-6 pt-6 border-t border-gray-200' : ''}>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                        Guías de Saldo Inicial ({initialBalanceGuides.length})
                      </h4>
                      <p className="text-xs text-gray-600 mb-3">
                        Seleccione una o más:
                      </p>
                      <div className="space-y-2">
                        {initialBalanceGuides.map((guide) => {
                          const isSelected = selectedGuideIds.includes(guide.id);
                          const item = guide.items && guide.items.length > 0 ? guide.items[0] : null;
                          return (
                            <div 
                              key={guide.id} 
                              className={`border rounded-lg p-2 transition-colors ${
                                item && item.available_balance <= 0.01
                                  ? 'border-amber-200 bg-amber-50/50'
                                  : isSelected 
                                    ? 'border-teal-500 bg-teal-50 cursor-pointer' 
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                              } ${isSelected ? 'border-teal-500 bg-teal-50' : ''}`}
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedGuideIds(selectedGuideIds.filter(id => id !== guide.id));
                                } else {
                                  setSelectedGuideIds([...selectedGuideIds, guide.id]);
                                }
                              }}
                            >
                              <div className="flex items-start gap-2">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  disabled={item && item.available_balance <= 0.01}
                                  onChange={() => {
                                    if (isSelected) {
                                      setSelectedGuideIds(selectedGuideIds.filter(id => id !== guide.id));
                                    } else {
                                      setSelectedGuideIds([...selectedGuideIds, guide.id]);
                                    }
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className="mt-1 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2 mb-1">
                                    <div className="flex items-center gap-2">
                                      <h4 className="font-semibold text-sm text-gray-800">{guide.code}</h4>
                                      <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                        {guide.status === 'issued' ? 'Emitida' : guide.status}
                                      </span>
                                    </div>
                                    <span className="text-xs text-gray-500">
                                      {guide.issue_date ? formatDateLocaleLima(guide.issue_date) : 'N/A'}
                                    </span>
                                  </div>
                                  {item && (
                                    <div className="space-y-1 text-xs mt-1">
                                      <div className="grid grid-cols-3 gap-2">
                                        <div>
                                          <span className="text-gray-500">Cantidad Original:</span>
                                          <span className="ml-1 font-medium text-gray-800">
                                            {parseFloat(item.quantity || 0).toFixed(3)} {item.unit_of_measure || ''}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-gray-500">Costo Unit.:</span>
                                          <span className="ml-1 font-medium text-gray-800">
                                            S/ {parseFloat(item.unit_price || 0).toFixed(2)}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-gray-500">Total:</span>
                                          <span className="ml-1 font-medium text-gray-800">
                                            S/ {parseFloat(item.total || 0).toFixed(2)}
                                          </span>
                                        </div>
                                      </div>
                                      {item.returned_quantity !== undefined && (
                                        <div className="flex gap-4 text-xs">
                                          <div>
                                            <span className="text-gray-500">Devuelto:</span>
                                            <span className="ml-1 font-medium text-orange-600">
                                              {parseFloat(item.returned_quantity || 0).toFixed(3)} {item.unit_of_measure || ''}
                                            </span>
                                          </div>
                                          <div>
                                            <span className="text-gray-500">Saldo Disponible:</span>
                                            <span className={`ml-1 font-bold ${
                                              parseFloat(item.available_balance || 0) > 0.01 
                                                ? 'text-green-600' 
                                                : 'text-amber-600'
                                            }`}>
                                              {parseFloat(item.available_balance || 0).toFixed(3)} {item.unit_of_measure || ''}
                                            </span>
                                            {parseFloat(item.available_balance || 0) <= 0.01 && (
                                              <span className="ml-1 text-xs text-amber-600">(solo referencia)</span>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {guide.warehouse && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      Almacén: {guide.warehouse.code}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            
            <div className="px-4 py-3 border-t border-gray-200 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {selectedGuideIds.length > 0 && (
                  <span>{selectedGuideIds.length} guía(s) seleccionada(s)</span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setIsReturnModalOpen(false);
                    setSelectedGuideIds([]);
                    setCurrentItemIndex(null);
                    if (!openedReturnModalFromSelectRef.current && currentItemIndex !== null) {
                      handleItemChange(currentItemIndex, 'kardex_event', 'compra');
                    }
                    openedReturnModalFromSelectRef.current = false;
                  }}
                  className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Cancelar
                </button>
                {(consumptionExitGuides.length > 0 || initialBalanceGuides.length > 0) && selectedGuideIds.length > 0 && (
                  <button
                    onClick={handleConfirmReturn}
                    className="px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded-md transition-colors"
                  >
                    Confirmar Devolución
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      );

  const machineryModalProductName = (() => {
    if (eventModalItemIndex == null) return '';
    const item = formData.items[eventModalItemIndex];
    if (!item || !item.product_id) return '';
    const product = findProductById(item.product_id, item.product);
    return product?.name || '';
  })();
  const machineryEventModal = isExit && (
    <MachineryEventModal
      isOpen={eventModalItemIndex !== null}
      onClose={() => setEventModalItemIndex(null)}
      onSuccess={(eventData) => {
        if (eventData && eventModalItemIndex != null) {
          setPendingMachineryEvents(prev => prev.filter(e => e.itemIndex !== eventModalItemIndex).concat([{ itemIndex: eventModalItemIndex, ...eventData }]));
          setLastEventFormByItem(prev => ({ ...prev, [eventModalItemIndex]: eventData }));
          setToast({ isOpen: true, message: 'Evento registrado. Se enviará con la guía.', type: 'success' });
        }
        setEventModalItemIndex(null);
      }}
      productName={machineryModalProductName}
      sourceEventDate={formData.entry_date || null}
      sourceEventTime={formData.entry_time || null}
      collectOnly={true}
      sourceProjectId={eventModalItemIndex != null && formData.items[eventModalItemIndex]?.project_id ? String(formData.items[eventModalItemIndex].project_id) : null}
      initialFormData={eventModalItemIndex != null ? (() => {
        const saved = lastEventFormByItem[eventModalItemIndex];
        const item = formData.items[eventModalItemIndex];
        const itemProjectId = item?.project_id != null && item.project_id !== '' ? String(item.project_id) : '';
        const savedProjectId = (saved?.project_id != null && saved.project_id !== '') ? saved.project_id : '';
        return { ...(saved || {}), project_id: itemProjectId || savedProjectId };
      })() : null}
    />
  );

  if (isPageMode) {
    return (
      <>
        {isInitialLoading ? <FormLoadingSpinner compact={!isPageMode} /> : formContent}
        <CreateSupplierModal
          isOpen={isCreateSupplierModalOpen}
          onClose={() => setIsCreateSupplierModalOpen(false)}
          onSuccess={handleSupplierCreated}
          forceProviderOnly={false}
        />
        {returnModal}
        {machineryEventModal}
        <GuideItemsImportPreviewModal
          isOpen={importPreview != null}
          onClose={() => setImportPreview(null)}
          onConfirm={handleConfirmImportPreview}
          fileName={importPreview?.fileName}
          items={importPreview?.items || []}
          errors={importPreview?.errors || []}
          warnings={importPreview?.warnings || []}
          isExit={isExit}
          projects={projects}
          {...productSelectorAsyncProps}
          units={units}
        />
        {isReception && entryGuide?.id && (
          <ReceptionConsumeModal
            isOpen={isConsumeModalOpen}
            guideId={entryGuide.id}
            guideCode={entryGuide.code}
            onClose={() => setIsConsumeModalOpen(false)}
            onSuccess={(data) => {
              const exitGuide = data?.exit_guide;
              const exitCode = exitGuide?.code;
              if (exitGuide?.id) {
                setReceptionConsumeTrace({ id: exitGuide.id, code: exitCode || null });
              }
              setToast({
                isOpen: true,
                message: exitCode
                  ? `Consumo registrado. Guía de salida ${exitCode} creada.`
                  : 'Consumo registrado exitosamente',
                type: 'success',
              });
            }}
            onError={(message) => {
              setToast({
                isOpen: true,
                message: message || 'Error al consumir la recepción',
                type: 'error',
              });
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${!isOpen ? 'hidden' : ''}`}>
        <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">
                {entryGuide ? 'Editar Guía de Entrada' : 'Nueva Guía de Entrada'}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          {isInitialLoading ? <FormLoadingSpinner compact={!isPageMode} /> : formContent}
        </div>
      </div>
      <CreateSupplierModal
        isOpen={isCreateSupplierModalOpen}
        onClose={() => setIsCreateSupplierModalOpen(false)}
        onSuccess={handleSupplierCreated}
        forceProviderOnly={false}
      />
      {returnModal}
      {machineryEventModal}
      <GuideItemsImportPreviewModal
        isOpen={importPreview != null}
        onClose={() => setImportPreview(null)}
        onConfirm={handleConfirmImportPreview}
        fileName={importPreview?.fileName}
        items={importPreview?.items || []}
        errors={importPreview?.errors || []}
        warnings={importPreview?.warnings || []}
        isExit={isExit}
        projects={projects}
        {...productSelectorAsyncProps}
        units={units}
      />
      {isReception && entryGuide?.id && (
        <ReceptionConsumeModal
          isOpen={isConsumeModalOpen}
          guideId={entryGuide.id}
          guideCode={entryGuide.code}
          onClose={() => setIsConsumeModalOpen(false)}
          onSuccess={(data) => {
            const exitGuide = data?.exit_guide;
            const exitCode = exitGuide?.code;
            if (exitGuide?.id) {
              setReceptionConsumeTrace({ id: exitGuide.id, code: exitCode || null });
            }
            setToast({
              isOpen: true,
              message: exitCode
                ? `Consumo registrado. Guía de salida ${exitCode} creada.`
                : 'Consumo registrado exitosamente',
              type: 'success',
            });
          }}
          onError={(message) => {
            setToast({
              isOpen: true,
              message: message || 'Error al consumir la recepción',
              type: 'error',
            });
          }}
        />
      )}
    </>
  );
};

export default EntryGuideForm;

